/**
 * View Event Modal Component
 * Enhanced view/edit functionality with archive, edit history, and field tracking
 */

import { AuthenticationService } from 'src/app/services/authentication.service'
import { OpsProduct, OpsService } from '../../../../../services/ops.service'

import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'

import { forkJoin, Observable, Subject } from 'rxjs'
import { map, startWith, takeUntil } from 'rxjs/operators'
import { Validators } from '@angular/forms'

import { MOCK_BATCHES } from '../../../data/mock-batches-data'
import { Batch, BatchNameAutocompleteItem, BatchResult } from '../../../models/batch.model'
import { BatchLinkCandidate } from '../../../models/batch-history.model'
import { BatchCustomFieldValue, Equipment, EquipmentCustomField } from '../../../models/equipment.model'
import {
  DowntimeEvent, DowntimeReason, DowntimeReasonGroup, DowntimeReasonType, EditHistoryEntry, EventLogResponse, MachineEvent,
  IncidentReason, IncidentReasonGroup, IncidentEvent, LinkedBatchEvent, LinkedDowntimeIncidentEvent
} from '../../../models/machine-event.model'
import { OEEDataService } from '../../../services/oee-data.service'
import { TimezoneService } from '../../../services/timezone.service'
import { AbortBatchModalComponent, AbortBatchModalResult } from '../abort-batch-modal/abort-batch-modal.component'
import { CompleteBatchModalComponent } from '../complete-batch-modal/complete-batch-modal.component'
import { ConfirmArchiveModalComponent, ConfirmArchiveResult } from '../confirm-archive-modal/confirm-archive-modal.component'
import { ConfirmResumeBatchModalComponent, ConfirmResumeBatchData } from '../confirm-resume-batch-modal/confirm-resume-batch-modal.component'

/** Database user interface for dropdown selection */
interface User {
    id: string;
    name: string;
    email: string;
    username: string;
    employee_number?: string;
}

@Component({
    selector: 'app-view-event-modal',
    templateUrl: './view-event-modal.component.html',
    styleUrls: ['./view-event-modal.component.scss'],
  standalone: false
})
export class ViewEventModalComponent implements OnInit, OnDestroy {
    /** Canonical display label for a batch.result value. */
    batchResultDisplayLabel(result: string | null | undefined): string {
        switch (result) {
            case 'running':
            case 'in_progress':
                return 'Active'
            case 'idle':             return 'Idle'
            case 'ready_for_review': return 'Ready for Review'
            case 'approved':         return 'Approved'
            case 'completed':        return 'Completed'
            case 'aborted':          return 'Aborted'
            case 'cancelled':        return 'Cancelled'
            case 'unstarted':
            case 'pending':
                return 'Unstarted'
            case 'success':          return 'Success'
            case 'failure':          return 'Failure'
            default:
                return result ? result.charAt(0).toUpperCase() + result.slice(1).replace(/_/g, ' ') : ''
        }
    }

    private destroy$ = new Subject<void>();
    private formDestroy$ = new Subject<void>(); // Completes on each form rebuild to prevent subscription leaks
    isEditMode = false;
    /** When true, cancel & save both CLOSE the dialog instead of returning to
     *  view mode. Set when the modal was opened with `_editOnly: true`
     *  (e.g. the batch-focus-page header "Edit" action) — the caller asked
     *  for an edit flow, so we don't drop them into a view they didn't ask
     *  for. */
    private editOnlyMode = false;
    readOnly = false;
    showEditHistory = false;
    editForm!: FormGroup;
    saving = false;
    archiving = false; // Track if archive operation is in progress
    restoring = false; // Track if restore operation is in progress
    reopening = false; // Track if reopen batch operation is in progress
    batchEdited = false; // Track if batch fields were modified
    batchCompleted = false; // Track if batch was completed from this modal
    eventArchiveChanged = false; // Track if event was archived/unarchived
    eventEdited = false; // Track if event data was modified (for dashboard refresh)

    /** Track whether user has manually edited scrap/reject_parts in edit form */
    private scrapTouched = false;
    /** Suppress programmatic valueChanges from triggering touch detection */
    private suppressScrapTouch = false;
    loadingBatches = false; // Track if batches are being loaded
    batchesSectionExpanded = true; // Track if batches section is expanded

    // Collapsible edit section states (default to expanded)
    editSectionExpanded: Record<string, boolean> = {};

    // Custom fields for batch events (inline display)
    customFields: EquipmentCustomField[] = [];
    customFieldValues: BatchCustomFieldValue[] = [];
    loadingCustomFields = false;

    // Batch number autocomplete
    batchNameItems: BatchNameAutocompleteItem[] = [];
    filteredBatchNames!: Observable<BatchNameAutocompleteItem[]>;

    @Output() batchCompletedEvent = new EventEmitter<Batch>();

    // Available downtime reasons for dropdown
    availableDowntimeReasons: DowntimeReason[] = [];
    // Available downtime reason groups for type lookup
    private downtimeGroups: DowntimeReasonGroup[] = [];

    // Available incident reasons for dropdown
    availableIncidentReasons: IncidentReason[] = [];
    // Available incident reason groups for group lookup
    incidentReasonGroups: IncidentReasonGroup[] = [];

    // Available Org users for user dropdowns
    availableUsers: User[] = [];

    // Available products for product dropdown (batch events)
    availableProducts: OpsProduct[] = [];
    loadingProducts = false;

    // Equipment details for print work order (code, location/system_name)
    equipmentDetails: {
        code?: string;
        name?: string;
        system_name?: string;
        equipment_number?: string;
        allows_batch_operators?: boolean;
        restrict_batch_equipment?: boolean;
        start_batch_text?: string;
        complete_batch_text?: string;
    } | null = null;
    equipmentDetailsLoaded = false;

    // Work order print mode - shows editable form instead of event details
    showWorkOrderForm = false;

    // Site ID for batch time entries (event doesn't carry site_id, so we use the selected site)
    selectedSiteId: string | null = null;

    // Multi-equipment editing (for downtime/incident events)
    allEquipment: Equipment[] = [];
    showMultiEquipmentSelect = false;
    equipmentSearchQuery = '';
    selectedEquipmentIds: string[] = [];

    // Event-batch linking (for downtime/incident events)
    linkedBatchEvents: LinkedBatchEvent[] = [];
    loadingLinkedBatches = false;
    showBatchLinkSelect = false;
    batchLinkSearchQuery = '';
    selectedBatchEventIds: string[] = [];
    availableBatchesForLinking: BatchLinkCandidate[] = [];
    loadingAvailableBatches = false;
    batchLinkPage = 1;
    batchLinkPageSize = 25;
    private dismissedBatchEventIds: string[] = []; // Previously dismissed temporal batch matches

    // Batch operator and equipment (derived from time entries)
    batchOperators: { user_id: string; display_name: string }[] = [];
    batchEquipment: { equipment_id: string; equipment_name: string; equipment_code: string }[] = [];

    // Worker notes (derived from time entries with notes across all stations for this job)
    workerNotes: { display_name: string; notes: string; start_ts: string; end_ts: string | null; equipment_name: string | null }[] = [];

    // Batch-event linking (for batch events: linked downtime/incidents)
    linkedDowntimeIncidentEvents: LinkedDowntimeIncidentEvent[] = [];
    loadingLinkedEvents = false;
    showEventLinkSelect = false;
    eventLinkSearchQuery = '';
    selectedLinkedEventIds: string[] = [];
    availableEventsForLinking: MachineEvent[] = [];
    loadingAvailableEvents = false;
    private dismissedEventIds: string[] = []; // Previously dismissed temporal event matches

    // Track which fields have been modified (from original values)
    modifiedFields = new Set<string>();

    // Original event values (before any edits in this session)
    private originalEvent: Partial<MachineEvent> = {};

    constructor(
        public dialogRef: MatDialogRef<ViewEventModalComponent>,
        // `_editOnly`: open straight into edit mode AND lock the modal into edit-only —
        // cancel and save both close the dialog, with no view-mode fallback.
        @Inject(MAT_DIALOG_DATA) public event: MachineEvent & { _readOnly?: boolean; _editOnly?: boolean },
        private fb: FormBuilder,
        private oeeDataService: OEEDataService,
        private dialog: MatDialog,
        private authService: AuthenticationService,
        private timezoneService: TimezoneService,
        private snackBar: MatSnackBar,
        private opsService: OpsService
    ) {
        // Set read-only mode if passed from parent (e.g. opening from associated lists)
        this.readOnly = !!event._readOnly;
        // Open directly in edit mode when the caller asks for it (e.g. header "Edit" actions).
        // Suppressed when read-only — the modal can't be edited anyway.
        if (event._editOnly && !this.readOnly) {
            this.isEditMode = true;
            this.editOnlyMode = true;
        }
        // Store original values for comparison
        this.originalEvent = {
            start_ts: event.start_ts,
            end_ts: event.end_ts,
            duration_minutes: event.duration_minutes,
            notes: event.notes,
        };

        if (event.type === 'downtime') {
            this.originalEvent.downtime_reason_id = event.downtime_reason_id;
            // //             this.originalEvent.cost = event.cost;
            this.originalEvent.troubleshooting_notes = event.troubleshooting_notes;
            this.originalEvent.resolution_notes = event.resolution_notes;
        }

        if (event.type === 'incident') {
            const incidentEvent = event as IncidentEvent;
            (this.originalEvent as Partial<IncidentEvent>).incident_reason_id = incidentEvent.incident_reason_id;
        }

        this.initEditForm();
        this.detectModifiedFields();
    }

    ngOnInit(): void {
        // Capture site ID for batch time entries
        this.selectedSiteId = this.oeeDataService.getSelectedSiteId();

        // Load Org users for user dropdowns
        this.oeeDataService.getOrganizationUsers().subscribe({
            next: (users) => {
                this.availableUsers = users
                if (this.event.event_id && (this.batchOperators.length > 0 || this.workerNotes.length > 0)) {
                    this.loadBatchAssignments()
                }
            },
            error: (err) => {
                console.error('Failed to load organization users:', err);
                this.snackBar.open('Failed to load users for assignment dropdowns', 'Dismiss', { duration: 5000 });
            }
        });

        // Load available downtime reasons and groups for the selected site
        const siteId = this.oeeDataService.getSelectedSiteId();

        // Load groups for type lookup
        this.oeeDataService.getDowntimeReasonGroups(siteId || undefined).subscribe({
            next: (groups) => {
                this.downtimeGroups = groups;
            },
            error: (err) => {
                console.error('❌ [ViewEventModal] Failed to load downtime groups:', err);
            }
        });

        this.oeeDataService.getDowntimeReasons(undefined, siteId || undefined).subscribe({
            next: (reasons) => {
                this.availableDowntimeReasons = reasons;

                // Populate downtime_reason object if missing but ID exists (for display)
                if (this.event.type === 'downtime' && this.event.downtime_reason_id) {
                    // Always try to refresh the downtime reason object to ensure it matches the ID
                    const reason = this.availableDowntimeReasons.find(r => r.reason_id === this.event.downtime_reason_id);
                    if (reason) {
                        this.event.downtime_reason = reason;
                    }
                }
            },
            error: (err) => {
                console.error('❌ [ViewEventModal] Failed to load downtime reasons:', err);
            }
        });

        // Load batches, custom fields, and batch name suggestions for batch events
        if (this.isBatchTypeEvent()) {
            this.loadBatchesForEvent();
            this.loadCustomFieldsForBatchEvent();
            this.loadBatchNameSuggestions();
            this.loadLinkedDowntimeIncidentEvents();
            this.loadProducts();
            this.loadBatchAssignments();
        }

        // Load incident reasons for incident events
        if (this.event.type === 'incident') {
            this.loadIncidentReasons();
        }

        // Load equipment details for work order printing (downtime and incident events)
        // Also loads all equipment for multi-equipment editing
        if (this.event.type === 'downtime' || this.event.type === 'incident') {
            this.loadEquipmentDetailsForPrint();
            this.initMultiEquipmentSelection();
            this.loadLinkedBatchEvents();
        }
    }

    /**
     * Initialize multi-equipment selection with current linked equipment
     */
    private initMultiEquipmentSelection(): void {
        // Pre-populate from event's linked_equipment_ids, falling back to equipment_id
        if (this.event.linked_equipment_ids && this.event.linked_equipment_ids.length > 0) {
            // Use Set to deduplicate and ensure equipment_id is included
            const equipmentIdSet = new Set<string>(this.event.linked_equipment_ids);
            equipmentIdSet.add(this.event.equipment_id);
            this.selectedEquipmentIds = Array.from(equipmentIdSet);
        } else {
            // Single equipment event
            this.selectedEquipmentIds = [this.event.equipment_id];
        }
        // Start collapsed - user can click Edit to expand
        this.showMultiEquipmentSelect = false;
    }

    /**
     * Load equipment details for print work order (code, system_name/location)
     */
    private loadEquipmentDetailsForPrint(): void {
        const siteId = this.oeeDataService.getSelectedSiteId();
        if (!siteId) {
            console.warn('[ViewEventModal] No site selected, cannot fetch equipment details for print');
            this.equipmentDetailsLoaded = true; // Mark as loaded (with no data)
            return;
        }

        // Fetch equipment and systems in parallel, then match them
        forkJoin({
            equipment: this.oeeDataService.getEquipment(siteId, true),
            systems: this.oeeDataService.getSystems(siteId)
        }).subscribe({
            next: ({ equipment: equipmentList, systems }) => {
                // Store all equipment for multi-equipment selection (exclude archived)
                this.allEquipment = equipmentList.filter(e => !e.is_archived);

                const equipment = equipmentList.find(e => e.id === this.event.equipment_id);
                if (equipment) {
                    // Find the system to get system_name
                    const system = systems.find(s => s.id === (equipment as any).system_id);
                    this.equipmentDetails = {
                        code: equipment.code,
                        name: equipment.name,
                        system_name: system?.name,
                        equipment_number: equipment.equipment_number,
                        allows_batch_operators: equipment.allows_batch_operators,
                        restrict_batch_equipment: equipment.restrict_batch_equipment,
                        start_batch_text: equipment.start_batch_text,
                        complete_batch_text: equipment.complete_batch_text
                    };
                }
                this.equipmentDetailsLoaded = true;
            },
            error: (err) => {
                console.warn('[ViewEventModal] Could not fetch equipment details for print:', err);
                this.equipmentDetailsLoaded = true; // Mark as loaded even on error
            }
        });
    }

    /**
     * Load incident reasons for incident events
     */
    private loadIncidentReasons(): void {
        const siteId = this.oeeDataService.getSelectedSiteId();

        this.oeeDataService.getIncidentReasonGroups(siteId || undefined).subscribe({
            next: (groups) => {
                this.incidentReasonGroups = groups;
                // Flatten all reasons from all groups
                this.availableIncidentReasons = ([] as IncidentReason[]).concat(
                    ...groups.map(g => g.reasons || [])
                );

                // Populate incident_reason object if missing but ID exists
                const incidentEvent = this.event as IncidentEvent;
                if (incidentEvent.incident_reason_id && !incidentEvent.incident_reason) {
                    const reason = this.availableIncidentReasons.find(r => r.id === incidentEvent.incident_reason_id);
                    if (reason) {
                        incidentEvent.incident_reason = reason;
                    }
                }
            },
            error: (err) => {
                console.error('❌ [ViewEventModal] Failed to load incident reasons:', err);
            }
        });
    }

    /**
     * Load batch name suggestions for autocomplete
     */
    private loadBatchNameSuggestions(): void {
        // Load batch names from the past 90 days for suggestions
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 90);

        this.oeeDataService.getAllBatchNames(
            this.oeeDataService.toISOString(startDate),
            this.oeeDataService.toISOString(endDate)
        ).subscribe({
            next: (items) => {
                this.batchNameItems = items;
                this.setupBatchNameAutocomplete();
            },
            error: (error) => {
                console.error('Failed to load batch name suggestions:', error);
                this.setupBatchNameAutocomplete();
            }
        });
    }

    /**
     * Load products for product dropdown (batch events)
     */
    private loadProducts(): void {
        if (!this.selectedSiteId) return;

        this.loadingProducts = true;
        this.opsService.getProducts(this.selectedSiteId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (products) => {
                this.availableProducts = products.filter(p => p.is_active);
                this.loadingProducts = false;
            },
            error: (error) => {
                console.error('Failed to load products:', error);
                this.loadingProducts = false;
            }
        });
    }

    /**
     * Setup batch name autocomplete filtering
     */
    private setupBatchNameAutocomplete(): void {
        const batchNumberControl = this.editForm?.get('batch_number');
        if (batchNumberControl) {
            this.filteredBatchNames = batchNumberControl.valueChanges.pipe(
                startWith(''),
                map(value => this._filterBatchNames(value || ''))
            );
        }
    }

    /**
     * Filter batch names based on user input
     */
    private _filterBatchNames(value: string): BatchNameAutocompleteItem[] {
        const filterValue = value.toLowerCase();
        return this.batchNameItems.filter(item =>
            item.batch_number.toLowerCase().includes(filterValue)
        );
    }

    /**
     * Format last used timestamp for batch name autocomplete
     */
    formatLastUsed(timestamp: string): string {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    /**
     * Load all batches associated with this batch event
     */
    private loadBatchesForEvent(): void {
        if (!this.event.event_id) return;
        this.loadingBatches = true;
        this.oeeDataService.getBatchesForProductionEvent(this.event.event_id).subscribe({
            next: (batches) => {
                this.event.batches = batches;
                // For backward compatibility, set batch to first batch if exists
                if (batches.length > 0 && !this.event.batch) {
                    this.event.batch = batches[0];
                }
                this.loadingBatches = false;
            },
            error: (err) => {
                console.error('❌ [ViewEventModal] Failed to load batches:', err);
                this.loadingBatches = false;
            }
        });
    }

    /**
     * Load custom fields and values for batch-type events
     * Only called for batch events in the new model (where batch_id = event_id)
     */
    private loadCustomFieldsForBatchEvent(): void {
        if (this.event.type !== 'batch' || !this.event.batch) return;

        this.loadingCustomFields = true;
        const equipmentId = this.event.equipment_id;
        const batchId = this.event.batch.batch_id;
        const eventId = this.event.event_id;

        forkJoin({
            fields: this.oeeDataService.getEquipmentCustomFields(equipmentId),
            values: this.oeeDataService.getBatchCustomFieldValues(batchId, eventId)
        }).subscribe({
            next: ({ fields, values }) => {
                this.customFields = fields;
                this.customFieldValues = values;
                this.loadingCustomFields = false;
                // Add custom field controls to the form for edit mode
                this.addCustomFieldControlsToForm();
            },
            error: (err) => {
                console.error('❌ [ViewEventModal] Failed to load custom fields:', err);
                this.loadingCustomFields = false;
            }
        });
    }

    initEditForm(): void {
        // Complete previous form subscriptions to prevent leaks when form is rebuilt
        this.formDestroy$.next();
        this.formDestroy$.complete();
        this.formDestroy$ = new Subject<void>();

        const isDowntime = this.event.type === 'downtime';
        const isBatchEvent = this.isBatchTypeEvent();
        const isIncident = this.event.type === 'incident';
        // Cast to any to access downtime-specific fields safely if type check passes
        const downtimeEvent = isDowntime ? (this.event as DowntimeEvent) : ({} as Partial<DowntimeEvent>);
        const incidentEvent = isIncident ? (this.event as IncidentEvent) : ({} as Partial<IncidentEvent>);
        const batch = isBatchEvent ? this.event.batch : null;

        // Note: DatetimePickerComponent handles UTC↔local conversion automatically,
        // so we pass raw UTC ISO strings directly (not pre-formatted local values)
        const formConfig: Record<string, unknown[]> = {
            start_ts: [this.event.start_ts || null],
            end_ts: [this.event.end_ts || null],
            // Duration is NOT included - it's calculated from start/end and should not be editable
            downtime_reason_id: [isDowntime ? downtimeEvent.downtime_reason_id : null],
            incident_reason_id: [isIncident ? incidentEvent.incident_reason_id : null],
            cost: [isDowntime ? (downtimeEvent as DowntimeEvent & { cost?: number }).cost : null],
            notes: [this.event.notes],
            troubleshooting_notes: [isDowntime ? downtimeEvent.troubleshooting_notes : null],
            resolution_notes: [isDowntime ? downtimeEvent.resolution_notes : null],
            // User assignment fields (for all editable events)
            created_by: [this.event.created_by || ''],
            troubleshooting_started_at: [isDowntime ? (downtimeEvent.troubleshooting_started_at || null) : null],
            troubleshooting_started_by: [isDowntime ? downtimeEvent.troubleshooting_started_by : null],
            resolved_at: [isDowntime ? (downtimeEvent.resolved_at || null) : null],
            resolved_by: [isDowntime ? downtimeEvent.resolved_by : null],
            // Batch fields for unified editing (batch-type events)
            product_id: [batch?.product_id || '', isBatchEvent ? [Validators.required] : []],
            batch_number: [batch?.batch_number || ''],
            work_order: [batch?.work_order || ''],
            good_parts: [batch?.good_parts ?? 0, [Validators.min(0)]],
            reject_parts: [batch?.reject_parts ?? 0, [Validators.min(0)]],
            completed_by: [this.event.completed_by || ''], // Completed By (now on machine_events)
            edit_comment: [''] // Optional comment explaining why edits were made
        };

        // Add custom field controls for batch events
        if (isBatchEvent && this.customFieldValues.length > 0) {
            this.customFieldValues.forEach(cfv => {
                formConfig[`custom_field_${cfv.custom_field_id}`] = [cfv.value || ''];
            });
        }

        this.editForm = this.fb.group(formConfig);

        // Re-validate end_ts when start_ts changes (to catch cases where start is changed after end)
        this.editForm.get('start_ts')?.valueChanges.pipe(takeUntil(this.formDestroy$)).subscribe(() => {
            const endTsControl = this.editForm.get('end_ts');
            if (endTsControl?.value) {
                endTsControl.updateValueAndValidity();
            }
        });

        // Add end time validator dynamically since it needs access to form
        this.editForm.get('end_ts')?.setValidators([this.endTimeAfterStartTimeValidator()]);
        this.editForm.get('end_ts')?.updateValueAndValidity();

        // Auto-scrap calculation for batch events
        if (isBatchEvent) {
            this.scrapTouched = false;
            const targetQty = batch?.target_quantity;
            if (targetQty) {
                this.editForm.get('good_parts')?.valueChanges.pipe(takeUntil(this.formDestroy$)).subscribe(goodParts => {
                    if (!this.scrapTouched && targetQty) {
                        const scrap = Math.max(0, targetQty - (Number(goodParts) || 0));
                        this.suppressScrapTouch = true;
                        this.editForm.get('reject_parts')?.setValue(scrap, { emitEvent: true });
                        this.suppressScrapTouch = false;
                    }
                });
                this.editForm.get('reject_parts')?.valueChanges.pipe(takeUntil(this.formDestroy$)).subscribe(() => {
                    if (!this.suppressScrapTouch) {
                        this.scrapTouched = true;
                    }
                });
            }
        }

        // Note: All troubleshooting fields are now always editable in edit mode
        // Users can set troubleshooting_started_at/by and resolved_at/by at any time
    }

    private endTimeAfterStartTimeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const endTs = control.value;
            const startTs = this.editForm?.get('start_ts')?.value;
            if (!endTs || !startTs) {
                return null;
            }
            const startDate = new Date(startTs);
            const endDate = new Date(endTs);
            if (endDate <= startDate) {
                return { invalidTimeRange: true };
            }
            return null;
        };
    }

    /**
     * Add custom field controls to the edit form after they are loaded
     * Called when custom fields finish loading for batch events
     */
    private addCustomFieldControlsToForm(): void {
        if (!this.editForm || this.event.type !== 'batch') return;

        this.customFieldValues.forEach(cfv => {
            const controlName = `custom_field_${cfv.custom_field_id}`;
            if (!this.editForm.get(controlName)) {
                this.editForm.addControl(controlName, this.fb.control(cfv.value || ''));
            }
        });

        // Also add controls for custom fields that don't have values yet
        this.customFields.forEach(cf => {
            const controlName = `custom_field_${cf.id}`;
            if (!this.editForm.get(controlName)) {
                const defaultValue = cf.type === 'checkbox' ? false : '';
                this.editForm.addControl(controlName, this.fb.control(defaultValue));
            }
        });
    }

    /**
     * Enable/disable troubleshooting fields based on event state.
     * - All 4 fields disabled if troubleshooting hasn't started
     * - troubleshooting_started_at/by enabled once troubleshooting has started
     * - resolved_at/by enabled only once event is resolved
     */
    private updateTroubleshootingFieldsState(): void {
        const hasTroubleshootingStarted = !!this.event.troubleshooting_started_at;
        const isResolved = !!this.event.resolved_at;

        // Troubleshooting fields - enabled once troubleshooting has started
        const troubleshootingFields = ['troubleshooting_started_at', 'troubleshooting_started_by'];
        troubleshootingFields.forEach(field => {
            const control = this.editForm.get(field);
            if (control) {
                if (hasTroubleshootingStarted) {
                    control.enable();
                } else {
                    control.disable();
                }
            }
        });

        // Resolution fields - enabled only once event is resolved
        const resolutionFields = ['resolved_at', 'resolved_by'];
        resolutionFields.forEach(field => {
            const control = this.editForm.get(field);
            if (control) {
                if (isResolved) {
                    control.enable();
                } else {
                    control.disable();
                }
            }
        });
    }

    detectModifiedFields(): void {
        this.modifiedFields.clear();
    }

    isFieldModified(fieldName: string): boolean {
        return this.modifiedFields.has(fieldName);
    }

    getFieldHistory(fieldName: string): EditHistoryEntry[] {
        return [];
    }

    /**
     * Get display name for a field
     */
    getFieldDisplayName(fieldName: string): string {
        const displayNames: { [key: string]: string } = {
            'start_ts': 'Start Time',
            'end_ts': 'End Time',
            'duration_minutes': 'Duration',
            'downtime_reason_id': 'Downtime Reason',
            'cost': 'Cost',
            'notes': 'Notes',
            'troubleshooting_notes': 'Troubleshooting Notes',
            'resolution_notes': 'Resolution Notes',
            'created_by': 'Created By',
            'troubleshooting_started_at': 'Troubleshooting Started At',
            'troubleshooting_started_by': 'Troubleshooting Started By',
            'resolved_at': 'Resolved At',
            'resolved_by': 'Resolved By',
            'work_order': 'Work Order',
            'result': 'Result',
            'good_batches': 'Good Batches',
            'failed_batches': 'Failed Batches',
            'part_count': 'Total Parts',
            'good_parts': 'Good Parts',
            'reject_parts': 'Reject Parts',
            'failure_reason_id': 'Failure Reason'
        };
        return displayNames[fieldName] || fieldName;
    }

    /**
     * Format field value for display
     */
    formatFieldValue(fieldName: string, value: string | null): string {
        if (value === null || value === undefined) return 'N/A';

        switch (fieldName) {
            case 'start_ts':
            case 'end_ts':
                return this.formatTimestamp(value);
            case 'duration_minutes':
                return this.formatDuration(Number(value));
            case 'downtime_reason_id':
            case 'failure_reason_id':
                const code = this.availableDowntimeReasons.find(r => r.reason_id === value);
                return code ? `${code.code} - ${code.display_name}` : `ID: ${value}`;
            case 'cost':
                return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
            case 'result':
                return String(value).charAt(0).toUpperCase() + String(value).slice(1);
            default:
                return String(value);
        }
    }

    toggleEditMode(): void {
        // Edit-only entry (e.g. header "Edit" action) — cancel closes the
        // dialog instead of dropping the user into a view they didn't ask for.
        if (this.isEditMode && this.editOnlyMode) {
            this.closeModal();
            return;
        }
        this.isEditMode = !this.isEditMode;
        if (!this.isEditMode) {
            // Reset form if canceling
            this.initEditForm();
        }
    }

    toggleEditHistory(): void {
        this.showEditHistory = !this.showEditHistory;
    }

    saveChanges(): void {
        if (this.editForm.invalid || this.saving) return;

        const isDowntime = this.event.type === 'downtime';
        const isIncident = this.event.type === 'incident';

        // Validate at least one equipment is selected for downtime/incident events
        if ((isDowntime || isIncident) && this.selectedEquipmentIds.length === 0) {
            this.snackBar.open('At least one equipment must be linked', 'Close', { duration: 5000 });
            return;
        }

        this.saving = true;
        const formValue = this.editForm.value; // Use .value (duration_minutes is readonly, not disabled)

        const isBatchEvent = this.isBatchTypeEvent();
        const downtimeEvent = isDowntime ? (this.event as DowntimeEvent) : null;
        const batch = isBatchEvent ? this.event.batch : null;

        // Validate timestamp order for downtime events
        if (isDowntime) {
            const startTs = formValue.start_ts ? new Date(formValue.start_ts) : null;
            const troubleshootingStartedAt = formValue.troubleshooting_started_at ? new Date(formValue.troubleshooting_started_at) : null;
            const resolvedAt = formValue.resolved_at ? new Date(formValue.resolved_at) : null;

            if (troubleshootingStartedAt && startTs && troubleshootingStartedAt < startTs) {
                this.snackBar.open('Troubleshooting cannot start before event start time', 'Close', { duration: 5000 });
                this.saving = false;
                return;
            }
            if (resolvedAt && troubleshootingStartedAt && resolvedAt < troubleshootingStartedAt) {
                this.snackBar.open('Resolution cannot be before troubleshooting started', 'Close', { duration: 5000 });
                this.saving = false;
                return;
            }
            if (resolvedAt && startTs && resolvedAt < startTs) {
                this.snackBar.open('Resolution cannot be before event start time', 'Close', { duration: 5000 });
                this.saving = false;
                return;
            }
        }

        // Convert datetime-local back to ISO strings using site timezone
        // The user enters times in the site's timezone, so we need to convert properly to UTC
        const request = {
            event_id: this.event.event_id,
            edited_by: this.authService.appMemberID || '',
            edit_comment: formValue.edit_comment || undefined,
            start_ts: formValue.start_ts ? (this.timezoneService.parseFromDateTimeInput(formValue.start_ts) || undefined) : undefined,
            end_ts: formValue.end_ts ? (this.timezoneService.parseFromDateTimeInput(formValue.end_ts) || undefined) : undefined,
            // Do NOT send duration_minutes - it's derived from start_ts and end_ts on the server
            downtime_reason_id: (isDowntime && formValue.downtime_reason_id !== downtimeEvent?.downtime_reason_id) ? formValue.downtime_reason_id : undefined,
            incident_reason_id: (this.event.type === 'incident' && formValue.incident_reason_id !== this.event.incident_reason_id) ? formValue.incident_reason_id : undefined,
            // Note: cost field is planned but not yet in the DowntimeEvent interface
            cost: (isDowntime && formValue.cost !== (this.event as MachineEvent & { cost?: number })?.cost) ? formValue.cost : undefined,
            notes: formValue.notes !== this.event.notes ? formValue.notes : undefined,
            troubleshooting_notes: (isDowntime && formValue.troubleshooting_notes !== downtimeEvent?.troubleshooting_notes) ? formValue.troubleshooting_notes : undefined,
            resolution_notes: (isDowntime && formValue.resolution_notes !== downtimeEvent?.resolution_notes) ? formValue.resolution_notes : undefined,
            // User assignment fields (for downtime events)
            created_by: formValue.created_by !== this.event.created_by ? formValue.created_by : undefined,
            troubleshooting_started_at: (isDowntime && formValue.troubleshooting_started_at)
                ? (this.timezoneService.parseFromDateTimeInput(formValue.troubleshooting_started_at) || undefined)
                : undefined,
            troubleshooting_started_by: (isDowntime && formValue.troubleshooting_started_by !== downtimeEvent?.troubleshooting_started_by)
                ? formValue.troubleshooting_started_by
                : undefined,
            resolved_at: (isDowntime && formValue.resolved_at)
                ? (this.timezoneService.parseFromDateTimeInput(formValue.resolved_at) || undefined)
                : undefined,
            resolved_by: (isDowntime && formValue.resolved_by !== downtimeEvent?.resolved_by)
                ? formValue.resolved_by
                : undefined,
            // Batch fields for unified editing (batch-type events)
            batch_number: (isBatchEvent && formValue.batch_number !== (batch?.batch_number || ''))
                ? formValue.batch_number
                : undefined,
            work_order: (isBatchEvent && formValue.work_order !== (batch?.work_order || ''))
                ? formValue.work_order
                : undefined,
            good_parts: (isBatchEvent && formValue.good_parts !== (batch?.good_parts ?? 0))
                ? formValue.good_parts
                : undefined,
            reject_parts: (isBatchEvent && formValue.reject_parts !== (batch?.reject_parts ?? 0))
                ? formValue.reject_parts
                : undefined,
            completed_by: (isBatchEvent && formValue.completed_by !== (this.event.completed_by || ''))
                ? formValue.completed_by
                : undefined,
            // Multi-equipment linking (for downtime/incident events)
            linked_equipment_ids: ((isDowntime || isIncident) && this.hasEquipmentLinksChanged())
                ? this.selectedEquipmentIds
                : undefined,
            // Event-batch linking (for downtime/incident events → batches)
            // Exclude auto-linked items so they remain ephemeral (recomputed on load)
            linked_batch_event_ids: ((isDowntime || isIncident) && this.hasBatchLinksChanged())
                ? this.selectedBatchEventIds.filter(id =>
                    !this.linkedBatchEvents.some(l => l.batch_event_id === id && l.auto_linked))
                : undefined,
            dismissed_batch_event_ids: ((isDowntime || isIncident) && this.hasBatchLinksChanged())
                ? [
                    ...this.dismissedBatchEventIds,
                    ...this.linkedBatchEvents
                        .filter(l => l.auto_linked && !this.selectedBatchEventIds.includes(l.batch_event_id))
                        .map(l => l.batch_event_id)
                ]
                : undefined,
            // Batch-event linking (for batch events → downtime/incidents)
            // Exclude auto-linked items so they remain ephemeral (recomputed on load)
            linked_event_ids: (isBatchEvent && this.hasEventLinksChanged())
                ? this.selectedLinkedEventIds.filter(id =>
                    !this.linkedDowntimeIncidentEvents.some(l => l.event_id === id && l.auto_linked))
                : undefined,
            dismissed_event_ids: (isBatchEvent && this.hasEventLinksChanged())
                ? [
                    ...this.dismissedEventIds,
                    ...this.linkedDowntimeIncidentEvents
                        .filter(l => l.auto_linked && !this.selectedLinkedEventIds.includes(l.event_id))
                        .map(l => l.event_id)
                ]
                : undefined
        };

        // Collect custom field values if this is a batch event
        const customFieldUpdates: { custom_field_id: string; value: string | null }[] = [];
        if (isBatchEvent) {
            this.customFields.forEach(cf => {
                const controlName = `custom_field_${cf.id}`;
                const newValue = formValue[controlName];
                const existingValue = this.customFieldValues.find(v => v.custom_field_id === cf.id)?.value || '';

                if (newValue !== existingValue) {
                    customFieldUpdates.push({
                        custom_field_id: cf.id,
                        value: cf.type === 'checkbox' ? String(newValue) : newValue
                    });
                }
            });
        }

        this.oeeDataService.editEvent(request).subscribe({
            next: (response) => {
                // Update local event with values from request (since backend returns void)
                const requestRecord = request as unknown as Record<string, unknown>;
                Object.keys(requestRecord).forEach(key => {
                    const value = requestRecord[key];
                    if (value !== undefined) {
                        (this.event as unknown as Record<string, unknown>)[key] = value;

                        // Also update batch object if it exists
                        if (this.event.batch && key in this.event.batch) {
                            (this.event.batch as unknown as Record<string, unknown>)[key] = value;
                        }
                    }
                });

                // Update resolved_at if returned (for resolution actions)
                const responseRecord = response as unknown as Record<string, unknown>;
                if (responseRecord['resolved_at']) {
                    this.event.resolved_at = responseRecord['resolved_at'] as string;
                    // Also update end_ts as resolution ends the downtime
                    this.event.end_ts = responseRecord['resolved_at'] as string;
                }

                // Always recalculate duration from the updated timestamps
                // This ensures duration reflects the new start/end times after any edit
                if (this.event.start_ts && this.event.end_ts) {
                    const start = new Date(this.event.start_ts).getTime();
                    const end = new Date(this.event.end_ts).getTime();
                    this.event.duration_minutes = Math.round((end - start) / (1000 * 60));
                }

                // Ensure downtime_reason object is populated for display (backend might only return ID)
                if (this.event.type === 'downtime' && this.event.downtime_reason_id) {
                    const reason = this.availableDowntimeReasons.find(r => r.reason_id === this.event.downtime_reason_id);
                    if (reason) {
                        this.event.downtime_reason = reason;
                    }
                }

                // Ensure incident_reason object is populated for display (backend might only return ID)
                if (this.event.type === 'incident') {
                    const incidentEvt = this.event as IncidentEvent;
                    if (incidentEvt.incident_reason_id) {
                        const reason = this.availableIncidentReasons.find(r => r.id === incidentEvt.incident_reason_id);
                        if (reason) {
                            incidentEvt.incident_reason = reason;
                        }
                    }
                }

                // Update user display names based on selected user IDs
                if (request.created_by) {
                    const user = this.availableUsers.find(u => u.id === request.created_by);
                    this.event.created_by_name = user?.name || user?.email || request.created_by;
                }
                if (this.event.type === 'downtime') {
                    const downtimeEvt = this.event as DowntimeEvent;
                    if (request.troubleshooting_started_by) {
                        const user = this.availableUsers.find(u => u.id === request.troubleshooting_started_by);
                        downtimeEvt.troubleshooting_started_by_name = user?.name || user?.email || request.troubleshooting_started_by;
                    }
                    if (request.resolved_by) {
                        const user = this.availableUsers.find(u => u.id === request.resolved_by);
                        downtimeEvt.resolved_by_name = user?.name || user?.email || request.resolved_by;
                    }
                }

                // Update linked equipment display data after save
                if (request.linked_equipment_ids && request.linked_equipment_ids.length > 0) {
                    this.event.linked_equipment_ids = [...request.linked_equipment_ids];
                    this.event.linked_equipment_names = this.allEquipment
                        .filter(eq => request.linked_equipment_ids!.includes(eq.id))
                        .map(eq => eq.name);

                    // Update equipment_id and equipment_name to the first equipment (backend does this too)
                    const firstEquipmentId = request.linked_equipment_ids[0];
                    const firstEquipment = this.allEquipment.find(eq => eq.id === firstEquipmentId);
                    if (firstEquipment) {
                        this.event.equipment_id = firstEquipment.id;
                        this.event.equipment_name = firstEquipment.name;
                    }
                }

                // Refresh linked batch events after save (for downtime/incident events)
                if (request.linked_batch_event_ids !== undefined) {
                    this.loadLinkedBatchEvents();
                }

                // Refresh linked downtime/incident events after save (for batch events)
                if (request.linked_event_ids !== undefined) {
                    this.loadLinkedDowntimeIncidentEvents();
                }

                if (isBatchEvent && batch && formValue.product_id !== (batch.product_id || '')) {
                    this.oeeDataService.updateBatch({
                        batch_id: batch.batch_id,
                        product_id: formValue.product_id
                    }).subscribe({
                        next: () => {
                            if (this.event.batch) {
                                this.event.batch.product_id = formValue.product_id;
                                const product = this.availableProducts.find(p => p.id === formValue.product_id);
                                if (product) {
                                    this.event.batch.product_name = product.product_name;
                                    this.event.batch.product_code = product.product_code;
                                }
                            }
                            this.batchEdited = true;
                        },
                        error: (err) => {
                            console.error('❌ [ViewEventModal] Failed to update product:', err);
                            this.snackBar.open('Event saved, but failed to update product', 'Close', { duration: 5000 });
                        }
                    });
                }

                // Save custom field values if there are any updates
                if (isBatchEvent && customFieldUpdates.length > 0 && batch) {
                    this.oeeDataService.saveBatchCustomFieldValues(
                        batch.batch_id,
                        this.event.event_id,
                        this.event.equipment_id,
                        customFieldUpdates
                    ).subscribe({
                        next: (savedValues) => {
                            // Update local custom field values
                            savedValues.forEach(sv => {
                                const existing = this.customFieldValues.find(v => v.custom_field_id === sv.custom_field_id);
                                if (existing) {
                                    existing.value = sv.value;
                                } else {
                                    this.customFieldValues.push(sv);
                                }
                            });
                        },
                        error: (err) => {
                            console.error('❌ [ViewEventModal] Failed to save custom fields:', err);
                            this.snackBar.open('Event saved, but failed to save custom fields', 'Close', { duration: 5000 });
                        }
                    });
                }

                // Exit edit mode
                this.isEditMode = false;
                this.saving = false;

                // Mark that event was edited for dashboard refresh
                this.eventEdited = true;

                // Refresh modified fields detection
                this.detectModifiedFields();

                // Show success toast
                this.snackBar.open('Event updated successfully', 'Dismiss', { duration: 3000 });

                // Edit-only entry — caller asked for an edit flow, so close once
                // the edit completes rather than falling back into view mode.
                if (this.editOnlyMode) {
                    this.closeModal();
                    return;
                }

                // Stay in view mode to show updated values (don't close dialog)
                // User can close manually after reviewing changes
            },
            error: (error) => {
                console.error('❌ [ViewEventModal] Failed to save:', error);
                this.saving = false;
                this.snackBar.open('Failed to save changes', 'Close', { duration: 5000 });
            }
        });
    }

    archiveEvent(): void {
        // Check if event has running batches (for warning in dialog)
        const hasRunningBatches = this.event.batches?.some(b => b.batch_status === 'running' || b.result === 'running') ||
            this.event.batch?.result === 'running';

        // Open confirmation dialog
        const dialogRef = this.dialog.open(ConfirmArchiveModalComponent, {
            width: '480px',
            panelClass: 'oee-mobile-modal',
            data: {
                eventType: this.event.type,
                eventId: this.event.event_id,
                hasRunningBatches,
                linkedEquipmentNames: this.event.linked_equipment_names
            }
        });

        dialogRef.afterClosed().subscribe((result: ConfirmArchiveResult) => {
            if (!result?.confirmed) return;

            this.archiving = true;

            const request = {
                event_id: this.event.event_id,
                archived_by: this.authService.appMemberID || '',
                archive_reason: result.reason
            };

            this.oeeDataService.archiveEvent(request).subscribe({
                next: (response) => {
                    this.archiving = false;
                    const archiveResponse = response as unknown as Record<string, unknown>;
                    // Update local event state with values from server response
                    this.event.is_archived = true;
                    this.event.archive_reason = request.archive_reason;
                    this.event.archived_at = archiveResponse['archived_at'] as string;
                    this.event.archived_by = archiveResponse['archived_by_out'] as string;
                    this.eventArchiveChanged = true; // Flag for dashboard refresh

                    // Show message with info about cancelled batches if any
                    if ((archiveResponse['cancelled_batch_count'] as number) > 0) {
                        this.snackBar.open(
                            `Event archived. ${archiveResponse['cancelled_batch_count']} running batch(es) cancelled: ${archiveResponse['cancelled_batch_numbers']}`,
                            'Dismiss',
                            { duration: 8000 }
                        );
                    } else {
                        this.snackBar.open('Event archived successfully', 'Dismiss', { duration: 3000 });
                    }
                },
                error: (error) => {
                    console.error('❌ [ViewEventModal] Failed to archive:', error);
                    this.archiving = false;
                    this.snackBar.open('Failed to archive event', 'Close', { duration: 5000 });
                }
            });
        });
    }

    unarchiveEvent(): void {
        this.restoring = true;

        const request = {
            event_id: this.event.event_id,
            unarchived_by: this.authService.appMemberID || ''
        };

        this.oeeDataService.unarchiveEvent(request).subscribe({
            next: (updatedEvent: any) => {
                this.restoring = false;

                // Preserve existing job data before merge
                const existingBatch = this.event.batch;

                // Update event with new archive status fields
                Object.assign(this.event, {
                    is_archived: updatedEvent.is_archived,
                    archive_reason: updatedEvent.archive_reason,
                    archived_by: updatedEvent.archived_by,
                    archived_at: updatedEvent.archived_at,
                    updated_at: updatedEvent.updated_at
                });

                // Merge job data from response (if present) with existing job data
                // The SQL function now returns batch details at the top level
                // completed_by is now on machine_events, not batch_details
                if (existingBatch || updatedEvent.batch_number !== undefined) {
                    this.event.batch = {
                        ...existingBatch,
                        batch_number: updatedEvent.batch_number ?? existingBatch?.batch_number,
                        work_order: updatedEvent.work_order ?? existingBatch?.work_order,
                        good_parts: updatedEvent.good_parts ?? existingBatch?.good_parts,
                        reject_parts: updatedEvent.scrap_parts ?? existingBatch?.reject_parts,
                        part_count: updatedEvent.total_parts ?? existingBatch?.part_count,
                        target_quantity: updatedEvent.target_quantity ?? existingBatch?.target_quantity
                    } as any;
                }
                // Update completed_by on the event (not batch)
                this.event.completed_by = updatedEvent.completed_by ?? this.event.completed_by;

                this.eventArchiveChanged = true; // Flag for dashboard refresh
                this.snackBar.open('Event restored successfully', 'Dismiss', { duration: 3000 });
            },
            error: (error) => {
                console.error('❌ [ViewEventModal] Failed to restore:', error);
                this.restoring = false;
                this.snackBar.open('Failed to restore event', 'Close', { duration: 5000 });
            }
        });
    }

    closeModal(): void {
        // Always return the updated event data so parent can refresh if needed
        const result = {
            success: true,
            batchCompleted: this.batchCompleted,
            batchEdited: this.batchEdited,
            eventArchiveChanged: this.eventArchiveChanged,
            eventEdited: this.eventEdited,
            event: this.event
        };
        this.dialogRef.close(result);
    }

    /**
     * Format timestamp for display using site timezone
     */
    formatTimestamp(timestamp?: string): string {
        if (!timestamp) return 'N/A';
        return this.timezoneService.formatTimestamp(timestamp, { dateStyle: 'short', timeStyle: 'short' });
    }

    /**
     * Get the timezone abbreviation for display
     */
    getTimezoneAbbreviation(): string {
        return this.timezoneService.getTimezoneAbbreviation();
    }

    /**
     * Get event duration in minutes
     */
    getDuration(): number | null {
        if (!this.event.start_ts || !this.event.end_ts) return null;
        const start = new Date(this.event.start_ts).getTime();
        const end = new Date(this.event.end_ts).getTime();
        return Math.round((end - start) / (1000 * 60));
    }

    /**
     * Format duration for display
     */
    formatDuration(durationMins?: number | null): string {
        // If duration_minutes is null/undefined, calculate from timestamps
        if (durationMins === null || durationMins === undefined) {
            if (this.event.start_ts && this.event.end_ts) {
                // Completed event: calculate from start to end
                const start = new Date(this.event.start_ts).getTime();
                const end = new Date(this.event.end_ts).getTime();
                durationMins = (end - start) / 60000;
            } else if (this.event.start_ts) {
                // Ongoing event (no end_ts): calculate elapsed time from start to now
                const start = new Date(this.event.start_ts).getTime();
                const now = Date.now();
                durationMins = (now - start) / 60000;
            } else {
                return 'Ongoing';
            }
        }

        if (durationMins < 1) {
            return '< 1 min';
        } else if (durationMins < 60) {
            return `${Math.round(durationMins)} min`;
        } else if (durationMins < 1440) {
            // Less than 24 hours: show hours and minutes
            const hours = Math.floor(durationMins / 60);
            const mins = Math.round(durationMins % 60);
            return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
        } else {
            // 24+ hours: show days and hours
            const days = Math.floor(durationMins / 1440);
            const hours = Math.round((durationMins % 1440) / 60);
            return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
        }
    }

    /**
     * Get event time display
     */
    getEventTime(): string {
        if (this.event.type === 'incident') {
            // Incidents may have start_ts or just created_at
            const ts = this.event.start_ts || this.event.created_at;
            return this.formatTimestamp(ts);
        } else {
            const start = this.formatTimestamp(this.event.start_ts);
            const end = this.event.end_ts ? this.formatTimestamp(this.event.end_ts) : 'Ongoing';
            return `${start} → ${end}`;
        }
    }

    /**
     * Get event type display label
     */
    getEventTypeDisplay(): string {
        if (this.event.type === 'downtime' && this.getDowntimeReasonType() === 'planned') {
            return 'PLANNED';
        }
        return this.event.type.toUpperCase();
    }

    /**
     * Get event type badge class
     */
    getEventTypeClass(): string {
        if (this.event.type === 'downtime' && this.getDowntimeReasonType() === 'planned') {
            return 'badge-planned';
        }
        return 'badge-' + this.event.type;
    }

    /**
     * Get dynamic modal title based on event type
     */
    getModalTitle(): string {
        switch (this.event.type) {
            case 'batch':
                return 'Job Details';
            case 'downtime':
                return 'Downtime Details';
            case 'incident':
                return 'Incident Details';
            default:
                return 'Event Details';
        }
    }

    /**
     * Check if this is a batch-type event (new model where batch_id = event_id).
     *
     * These events should show batch details inline, not in a separate section.
     *
     * API naming note:
     * - Newer APIs and UI code use 'batch' as the canonical event.type for this case.
     * - Some legacy or upstream endpoints may still return 'production' for the same
     *   logical concept.
     *
     * To remain backward compatible with existing data and mixed API responses, this
     * helper treats both 'batch' and 'production' as "batch-type" events. When adding
     * new features, prefer emitting and persisting 'batch' as the event type.
     */
    isBatchTypeEvent(): boolean {
        return this.event.type === 'batch' || this.event.type === 'production';
    }

    /** Canonical JOB identifier for the attachments section.
     *
     *  Attachments are scoped to the JOB (batch_number), not to individual
     *  machine events. A single job that flows through multiple stations
     *  produces multiple machine events / batch_id records — but the user
     *  expects one shared attachments list for the whole job. So we key off
     *  `batch.batch_number` here, not `batch_id` / `event_id` (which are
     *  per-leg). Falls through `event.batch` then `event.batches[0]` to
     *  tolerate openers that populate one but not the other. */
    resolvedBatchNumber(): string | null {
        if (!this.event || !this.isBatchTypeEvent()) return null;
        return this.event.batch?.batch_number ?? this.event.batches?.[0]?.batch_number ?? null;
    }

    get isStationEvent(): boolean {
        return !!(this.event as any)?._stationEvent;
    }

    get stationName(): string | undefined {
        return (this.event as any)?.station_name;
    }

    /**
     * Check if the batch is completed (has end_ts or batch.result === 'completed')
     */
    isCompletedBatch(): boolean {
        if (!this.isBatchTypeEvent()) return false;
        // Check multiple indicators of completion:
        // 1. Event has end_ts
        // 2. Batch has end_ts
        // 3. Batch result is 'completed'
        return !!this.event.end_ts ||
               !!this.event.batch?.end_ts ||
               this.event.batch?.result === 'completed' ||
               this.event.batch?.result === 'approved' ||
               this.event.batch?.result === 'aborted';
    }

    /**
     * Handle clearing the end date to return batch to in-progress state
     * Shows confirmation dialog first
     */
    onClearEndDate(): void {
        const dialogData: ConfirmResumeBatchData = {
            batchNumber: this.event.batch?.batch_number || undefined,
            equipmentName: this.event.equipment_name
        };

        const confirmRef = this.dialog.open(ConfirmResumeBatchModalComponent, {
            data: dialogData,
            width: '450px',
            panelClass: 'oee-mobile-modal'
        });

        confirmRef.afterClosed().subscribe((confirmed: boolean) => {
            if (!confirmed) return;

            const onSuccess = (toastMsg: string) => {
                this.snackBar.open(toastMsg, 'Close', { duration: 3000 });
                this.event.end_ts = undefined;
                this.event.resolved_at = undefined;
                this.event.resolved_by = undefined;
                this.event.resolution_notes = undefined;
                if (this.event.batch) {
                    this.event.batch.end_ts = undefined;
                    this.event.batch.result = 'running';
                }
                this.eventEdited = true;
                this.closeModal();
            };

            const onError = (err: unknown, action: string) => {
                console.error(`Failed to ${action}:`, err);
                this.snackBar.open(`Failed to ${action}`, 'Close', { duration: 3000 });
            };

            // Post-PR-3059 jobs go through resolve_machine_event, so most "completed" batches
            // also have resolved_at set. update_batch(clear_end_ts) does NOT clear resolution
            // fields, so use un_resolve_machine_event for the full reverse when applicable.
            if (this.event.resolved_at) {
                this.opsService.unResolveMachineEvent(this.event.event_id).subscribe({
                    next: () => onSuccess('Job un-resolved'),
                    error: (err) => onError(err, 'un-resolve job')
                });
            } else {
                this.oeeDataService.updateBatch({
                    batch_id: this.event.event_id,
                    clear_end_ts: true
                }).subscribe({
                    next: () => onSuccess('Job resumed - end date cleared'),
                    error: (err) => onError(err, 'resume job')
                });
            }
        });
    }

    /**
     * Check if this event should show the batches section
     * For batch events: hidden (details shown inline)
     * For downtime events: show overlapping batches for context
     */
    shouldShowBatchesSection(): boolean {
        if (this.event.type === 'batch') return false; // Batch details shown inline
        if (this.event.type === 'downtime') {
            // Show overlapping batches for context
            return (this.event.batches?.length || 0) > 0;
        }
        return false;
    }

    /**
     * Calculate parts per hour rate for batch events
     * Returns null if event is ongoing or has no parts data
     */
    calculatePartsPerHour(): number | null {
        if (this.event.type !== 'batch') return null;
        if (!this.event.end_ts || !this.event.start_ts) return null; // Ongoing

        const batch = this.event.batch;
        if (!batch) return null;

        const totalParts = (batch.good_parts || 0) + (batch.reject_parts || 0);
        if (totalParts === 0) return null;

        const durationHours = (this.event.duration_minutes || 0) / 60;
        if (durationHours === 0) return null;

        return Math.round((totalParts / durationHours) * 10) / 10; // 1 decimal place
    }

    /**
     * Get batch status display for simplified badges (Running/Completed/Aborted)
     */
    getSimplifiedBatchStatus(): 'Active' | 'Idle' | 'Ready for Review' | 'Approved' | 'Completed' | 'Aborted' | null {
        if (!this.event.batch) return null;
        const result = this.event.batch.result;
        if (result === 'running' || result === 'in_progress') return 'Active';
        if (result === 'idle') return 'Idle';
        if (result === 'ready_for_review') return 'Ready for Review';
        if (result === 'approved') return 'Approved';
        if (result === 'completed') return 'Completed';
        if (result === 'aborted') return 'Aborted';
        return null;
    }

    /**
     * Get the reason type by looking up through the group
     */
    getDowntimeReasonType(): DowntimeReasonType | undefined {
        if (this.event.type !== 'downtime') return undefined;
        if (this.event.downtime_reason_type) return this.event.downtime_reason_type;
        const reason = this.event.downtime_reason;
        if (!reason?.group_id) return undefined;
        const group = this.downtimeGroups.find(g => g.group_id === reason.group_id);
        return group?.type;
    }

    /**
     * Get the group name for the current downtime reason
     */
    getGroupName(): string | undefined {
        if (this.event.type !== 'downtime') return undefined;
        const reason = this.event.downtime_reason;
        if (!reason?.group_id) return undefined;
        const group = this.downtimeGroups.find(g => g.group_id === reason.group_id);
        return group?.name;
    }

    /**
     * Check if event is ongoing (no end time)
     */
    isOngoing(): boolean {
        return !this.event.end_ts;
    }

    /**
     * Check if event is archived
     */
    isArchived(): boolean {
        return !!this.event.is_archived;
    }

    /**
     * Check if event has linked equipment (multi-equipment event)
     * Returns true only when MORE than one equipment is linked (true multi-equipment)
     */
    hasLinkedEquipment(): boolean {
        return !!(this.event.linked_equipment_ids && this.event.linked_equipment_ids.length > 1);
    }

    // =========================================
    // MULTI-EQUIPMENT EDITING METHODS
    // =========================================

    /**
     * Toggle multi-equipment selection mode (expand/collapse the equipment list)
     */
    toggleMultiEquipment(): void {
        this.showMultiEquipmentSelect = !this.showMultiEquipmentSelect;
        // Clear search when closing
        if (!this.showMultiEquipmentSelect) {
            this.equipmentSearchQuery = '';
        }
    }

    /**
     * Get all equipment filtered by search query
     */
    getFilteredEquipment(): Equipment[] {
        if (!this.equipmentSearchQuery) return this.allEquipment;

        const query = this.equipmentSearchQuery.toLowerCase();
        return this.allEquipment.filter(eq =>
            eq.name.toLowerCase().includes(query) ||
            (eq.code && eq.code.toLowerCase().includes(query))
        );
    }

    /**
     * Check if equipment is selected
     */
    isEquipmentSelected(equipmentId: string): boolean {
        return this.selectedEquipmentIds.includes(equipmentId);
    }

    /**
     * Toggle equipment selection
     * Prevents deselecting the last equipment
     */
    toggleEquipmentSelection(equipmentId: string, event?: Event): void {
        const index = this.selectedEquipmentIds.indexOf(equipmentId);
        if (index > -1) {
            // Prevent removing the last equipment
            if (this.selectedEquipmentIds.length <= 1) {
                event?.preventDefault(); // Prevent the checkbox from visually unchecking
                this.snackBar.open('At least one equipment must be linked', 'Dismiss', { duration: 3000 });
                return;
            }
            this.selectedEquipmentIds.splice(index, 1);
        } else {
            this.selectedEquipmentIds.push(equipmentId);
        }
    }

    /**
     * Select all equipment (filtered if search is active)
     */
    selectAllEquipment(): void {
        const filteredIds = this.getFilteredEquipment().map(eq => eq.id);
        // Add all filtered equipment to selection (keeping existing selections outside filter)
        const existingNonFiltered = this.selectedEquipmentIds.filter(id =>
            !filteredIds.includes(id)
        );
        this.selectedEquipmentIds = [...new Set([...existingNonFiltered, ...filteredIds])];
    }

    /**
     * Get names of selected equipment (for display)
     */
    getSelectedEquipmentNames(): string[] {
        return this.allEquipment
            .filter(eq => this.selectedEquipmentIds.includes(eq.id))
            .map(eq => eq.name);
    }

    /**
     * Check if equipment links have been modified from original
     */
    hasEquipmentLinksChanged(): boolean {
        const originalIds = this.event.linked_equipment_ids || [this.event.equipment_id];
        const sortedOriginal = [...originalIds].sort();
        const sortedSelected = [...this.selectedEquipmentIds].sort();

        if (sortedOriginal.length !== sortedSelected.length) return true;
        return !sortedOriginal.every((id, i) => id === sortedSelected[i]);
    }

    /**
     * Check if equipment selection is valid (at least one selected)
     * Only applies to downtime and incident events
     */
    isEquipmentSelectionValid(): boolean {
        if (this.event.type !== 'downtime' && this.event.type !== 'incident') {
            return true; // Not applicable for other event types
        }
        return this.selectedEquipmentIds.length > 0;
    }

    // ======= Event-Batch Linking Methods =======

    /**
     * Load batch events linked to this downtime/incident event.
     * Also auto-associates temporally overlapping batches from the same equipment.
     */
    private loadLinkedBatchEvents(): void {
        if (!this.event.event_id) {
            console.warn('[ViewEventModal] Cannot load event batch links: no event_id');
            return;
        }
        this.loadingLinkedBatches = true;

        // The endpoint now returns the full association set (manual active +
        // dismissed + auto-temporal), computed from the same SQL helper that
        // backs the table's "Associated" badge — so the modal list and badge
        // can never disagree.
        this.oeeDataService.getEventBatchLinks(this.event.event_id).subscribe({
            next: (links) => {
                const visible = links.filter(l => !l.is_dismissed);
                this.dismissedBatchEventIds = links
                    .filter(l => l.is_dismissed)
                    .map(l => l.batch_event_id);

                this.linkedBatchEvents = visible;
                this.selectedBatchEventIds = visible.map(l => l.batch_event_id);
                this.loadingLinkedBatches = false;
            },
            error: (err) => {
                console.error('❌ [ViewEventModal] Failed to load linked batch events:', err);
                this.loadingLinkedBatches = false;
            }
        });
    }

    /**
     * Load downtime/incident events linked to this batch event.
     * Also auto-associates temporally overlapping downtime/incident events from the same equipment.
     */
    private loadLinkedDowntimeIncidentEvents(): void {
        if (!this.event.event_id) {
            console.warn('[ViewEventModal] Cannot load batch event links: no event_id');
            return;
        }
        this.loadingLinkedEvents = true;

        // The endpoint now returns the full association set (manual active +
        // dismissed + auto-temporal), computed from the same SQL helper that
        // backs the table's "Associated" badge — so the modal list and badge
        // can never disagree.
        this.oeeDataService.getBatchEventLinks(this.event.event_id).subscribe({
            next: (links) => {
                const visible = links.filter(l => !l.is_dismissed);
                this.dismissedEventIds = links
                    .filter(l => l.is_dismissed)
                    .map(l => l.event_id);

                this.linkedDowntimeIncidentEvents = visible;
                this.selectedLinkedEventIds = visible.map(l => l.event_id);
                this.loadingLinkedEvents = false;
            },
            error: (err) => {
                console.error('❌ [ViewEventModal] Failed to load linked downtime/incident events:', err);
                this.loadingLinkedEvents = false;
            }
        });
    }

    /**
     * Load available batches from the same site for linking
     */
    loadAvailableBatchesForLinking(): void {
        if (this.availableBatchesForLinking.length > 0) return; // Already loaded
        this.loadingAvailableBatches = true;
        const siteId = this.oeeDataService.getSelectedSiteId();
        if (!siteId) {
            this.loadingAvailableBatches = false;
            return;
        }

        // Get all batches for all equipment at this site (last 90 days)
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 90);

        this.oeeDataService.getSiteBatchHistory(
            siteId,
            this.oeeDataService.toISOString(startDate),
            this.oeeDataService.toISOString(endDate),
            0,
            500
        ).subscribe({
            next: (response) => {
                const fetched: BatchLinkCandidate[] = (response.items || [])
                    .filter(b => b.batch_id !== this.event.event_id)
                    .map(b => ({
                        batch_id: b.batch_id,
                        batch_number: b.batch_number,
                        work_order: b.work_order,
                        equipment_id: b.equipment_id,
                        equipment_name: b.equipment_name,
                        start_ts: b.start_ts,
                        end_ts: b.end_ts,
                        result: b.result,
                    }));

                // Merge in already-linked batches that may not be in the fetched results
                const fetchedIds = new Set(fetched.map(b => b.batch_id));
                for (const linked of this.linkedBatchEvents) {
                    if (!fetchedIds.has(linked.batch_event_id)) {
                        fetched.push({
                            batch_id: linked.batch_event_id,
                            batch_number: linked.batch_number,
                            work_order: linked.work_order || null,
                            equipment_id: linked.equipment_id,
                            equipment_name: linked.equipment_name,
                            start_ts: linked.start_ts,
                            end_ts: linked.end_ts || null,
                            result: linked.status,
                        });
                    }
                }

                this.availableBatchesForLinking = fetched;
                this.loadingAvailableBatches = false;
            },
            error: (err: any) => {
                console.error('❌ [ViewEventModal] Failed to load available batches:', err);
                this.loadingAvailableBatches = false;
            }
        });
    }

    /**
     * Load available downtime/incident events from the same equipment for linking
     */
    loadAvailableEventsForLinking(): void {
        if (this.availableEventsForLinking.length > 0) return; // Already loaded
        this.loadingAvailableEvents = true;

        // Get events from the primary equipment (last 90 days)
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 90);

        this.oeeDataService.getMachineEvents(
            this.event.equipment_id,
            this.oeeDataService.toISOString(startDate),
            this.oeeDataService.toISOString(endDate),
            undefined,
            1000,
            0
        ).subscribe({
            next: (response: EventLogResponse) => {
                const events: MachineEvent[] = response.events ?? [];
                // Filter to only downtime and incident events, exclude self
                const filtered = events.filter(
                    e => (e.type === 'downtime' || e.type === 'incident') && e.event_id !== this.event.event_id
                );

                // Merge in already-linked events that may be from different equipment
                const fetchedIds = new Set(filtered.map(e => e.event_id));
                for (const linked of this.linkedDowntimeIncidentEvents) {
                    if (!fetchedIds.has(linked.event_id)) {
                        filtered.push({
                            event_id: linked.event_id,
                            type: linked.event_type,
                            equipment_id: linked.equipment_id,
                            equipment_name: linked.equipment_name,
                            start_ts: linked.start_ts,
                            end_ts: linked.end_ts,
                            duration_minutes: linked.duration_minutes,
                            notes: linked.notes,
                        } as MachineEvent);
                    }
                }

                this.availableEventsForLinking = filtered;
                this.loadingAvailableEvents = false;
            },
            error: (err: any) => {
                console.error('❌ [ViewEventModal] Failed to load available events:', err);
                this.loadingAvailableEvents = false;
            }
        });
    }

    /**
     * Toggle batch link selection panel
     */
    toggleBatchLinkSelect(): void {
        this.showBatchLinkSelect = !this.showBatchLinkSelect;
        if (this.showBatchLinkSelect) {
            this.batchLinkPage = 1;
            this.loadAvailableBatchesForLinking();
        } else {
            this.batchLinkSearchQuery = '';
            this.batchLinkPage = 1;
        }
    }

    /**
     * Toggle event link selection panel (for batch events)
     */
    toggleEventLinkSelect(): void {
        this.showEventLinkSelect = !this.showEventLinkSelect;
        if (this.showEventLinkSelect) {
            this.loadAvailableEventsForLinking();
        } else {
            this.eventLinkSearchQuery = '';
        }
    }

    /**
     * Get filtered batches for linking (search by batch number, work order, equipment name)
     */
    getFilteredBatchesForLinking(): BatchLinkCandidate[] {
        if (!this.batchLinkSearchQuery) return this.availableBatchesForLinking;
        const query = this.batchLinkSearchQuery.toLowerCase();
        return this.availableBatchesForLinking.filter(b =>
            (b.batch_number && b.batch_number.toLowerCase().includes(query)) ||
            (b.work_order && b.work_order.toLowerCase().includes(query)) ||
            (b.equipment_name && b.equipment_name.toLowerCase().includes(query))
        );
    }

    getPaginatedBatchesForLinking(): BatchLinkCandidate[] {
        const filtered = this.getFilteredBatchesForLinking();
        const start = (this.batchLinkPage - 1) * this.batchLinkPageSize;
        return filtered.slice(start, start + this.batchLinkPageSize);
    }

    getBatchLinkTotalPages(): number {
        return Math.max(1, Math.ceil(this.getFilteredBatchesForLinking().length / this.batchLinkPageSize));
    }

    setBatchLinkPage(page: number): void {
        const total = this.getBatchLinkTotalPages();
        this.batchLinkPage = Math.max(1, Math.min(page, total));
    }

    /**
     * Get filtered events for linking (search by reason, equipment name)
     */
    getFilteredEventsForLinking(): MachineEvent[] {
        if (!this.eventLinkSearchQuery) return this.availableEventsForLinking;
        const query = this.eventLinkSearchQuery.toLowerCase();
        return this.availableEventsForLinking.filter(e => {
            const reason = e.type === 'downtime'
                ? (e as DowntimeEvent).downtime_reason?.display_name
                : (e as IncidentEvent).incident_reason?.name;
            return (
                (reason && reason.toLowerCase().includes(query)) ||
                (e.equipment_name && e.equipment_name.toLowerCase().includes(query)) ||
                (e.notes && e.notes.toLowerCase().includes(query))
            );
        });
    }

    /**
     * Check if a batch is selected for linking
     */
    isBatchEventSelected(batchEventId: string): boolean {
        return this.selectedBatchEventIds.includes(batchEventId);
    }

    /**
     * Toggle batch event selection
     */
    toggleBatchEventSelection(batchEventId: string): void {
        const index = this.selectedBatchEventIds.indexOf(batchEventId);
        if (index > -1) {
            this.selectedBatchEventIds.splice(index, 1);
        } else {
            this.selectedBatchEventIds.push(batchEventId);
        }
    }

    /**
     * Check if a linked event is selected
     */
    isLinkedEventSelected(eventId: string): boolean {
        return this.selectedLinkedEventIds.includes(eventId);
    }

    /**
     * Toggle linked event selection
     */
    toggleLinkedEventSelection(eventId: string): void {
        const index = this.selectedLinkedEventIds.indexOf(eventId);
        if (index > -1) {
            this.selectedLinkedEventIds.splice(index, 1);
        } else {
            this.selectedLinkedEventIds.push(eventId);
        }
    }

    /**
     * Select all filtered batches for linking
     */
    selectAllBatchesForLinking(): void {
        const filteredIds = this.getFilteredBatchesForLinking().map(b => b.batch_id);
        const existing = this.selectedBatchEventIds.filter(id => !filteredIds.includes(id));
        this.selectedBatchEventIds = [...new Set([...existing, ...filteredIds])];
    }

    /**
     * Clear all batch selections
     */
    clearBatchSelections(): void {
        this.selectedBatchEventIds = [];
    }

    /**
     * Select all filtered events for linking
     */
    selectAllEventsForLinking(): void {
        const filteredIds = this.getFilteredEventsForLinking().map(e => e.event_id);
        const existing = this.selectedLinkedEventIds.filter(id => !filteredIds.includes(id));
        this.selectedLinkedEventIds = [...new Set([...existing, ...filteredIds])];
    }

    /**
     * Clear all event selections
     */
    clearEventSelections(): void {
        this.selectedLinkedEventIds = [];
    }

    /**
     * Check if batch links have changed
     */
    hasBatchLinksChanged(): boolean {
        const originalIds = this.linkedBatchEvents.map(l => l.batch_event_id).sort();
        const selectedSorted = [...this.selectedBatchEventIds].sort();
        if (originalIds.length !== selectedSorted.length) return true;
        return !originalIds.every((id, i) => id === selectedSorted[i]);
    }

    /**
     * Check if event links have changed (for batch events)
     */
    hasEventLinksChanged(): boolean {
        const originalIds = this.linkedDowntimeIncidentEvents.map(l => l.event_id).sort();
        const selectedSorted = [...this.selectedLinkedEventIds].sort();
        if (originalIds.length !== selectedSorted.length) return true;
        return !originalIds.every((id, i) => id === selectedSorted[i]);
    }

    /**
     * Get the event description for display in the list
     */
    getEventDescription(event: MachineEvent): string {
        if (event.type === 'downtime') {
            const reason = (event as DowntimeEvent).downtime_reason?.display_name;
            return reason || 'No reason specified';
        } else if (event.type === 'incident') {
            const reason = (event as IncidentEvent).incident_reason?.name;
            return reason || 'No reason specified';
        }
        return event.type;
    }

    /**
     * Open a linked batch event in a new view-only modal
     */
    openLinkedBatch(batch: LinkedBatchEvent): void {
        const batchEvent: MachineEvent = {
            event_id: batch.batch_event_id,
            type: 'batch',
            equipment_id: batch.equipment_id,
            equipment_name: batch.equipment_name,
            start_ts: batch.start_ts,
            end_ts: batch.end_ts,
            created_by: '',
            batch: {
                batch_id: batch.batch_event_id,
                batch_number: batch.batch_number,
                work_order: batch.work_order,
                equipment_id: batch.equipment_id,
                good_parts: batch.good_parts ?? 0,
                reject_parts: batch.scrap_parts ?? 0,
                part_count: batch.total_parts ?? 0,
                start_ts: batch.start_ts,
                end_ts: batch.end_ts,
                result: (batch.status || 'running') as BatchResult
            } as Batch
        } as MachineEvent;

        this.dialog.open(ViewEventModalComponent, {
            width: '800px',
            maxWidth: '95vw',
            panelClass: 'oee-mobile-modal',
            data: { ...batchEvent, _readOnly: true }
        });
    }

    /**
     * Open a linked downtime/incident event in a new view-only modal
     */
    openLinkedEvent(evt: LinkedDowntimeIncidentEvent): void {
        const machineEvent: MachineEvent = {
            event_id: evt.event_id,
            type: evt.event_type,
            equipment_id: evt.equipment_id,
            equipment_name: evt.equipment_name,
            start_ts: evt.start_ts,
            end_ts: evt.end_ts,
            duration_minutes: evt.duration_minutes,
            notes: evt.notes,
            created_by: ''
        } as MachineEvent;

        this.dialog.open(ViewEventModalComponent, {
            width: '800px',
            maxWidth: '95vw',
            panelClass: 'oee-mobile-modal',
            data: { ...machineEvent, _readOnly: true }
        });
    }

    /**
     * Check if the save button should be enabled
     */
    canSave(): boolean {
        if (this.saving) return false;
        if (this.editForm.invalid) return false;
        if (!this.isEquipmentSelectionValid()) return false;
        return true;
    }

    /**
     * Check if event can be edited
     * Business rules: not locked, not archived, and type is downtime, batch, or incident
     */
    canEdit(): boolean {
        if (this.readOnly) return false;
        if (this.isArchived()) return false;

        // Downtime, batch, and incident events can be edited
        return this.event.type === 'downtime' || this.event.type === 'batch' || this.event.type === 'incident';
    }

    /**
     * Get the group name for the current incident reason
     */
    getIncidentGroupName(): string | undefined {
        if (this.event.type !== 'incident') return undefined;
        const reason = this.event.incident_reason;
        if (!reason?.group_id) return undefined;
        const group = this.incidentReasonGroups.find(g => g.id === reason.group_id);
        return group?.name;
    }

    /**
     * Check if event has edit history
     */
    hasEditHistory(): boolean {
        return false;
    }

    /**
     * Get sorted edit history (most recent first)
     */
    getEditHistory(): EditHistoryEntry[] {
        return [];
    }

    /**
     * Get the name of who archived the event
     * Resolves user ID to display name if available
     */
    getArchivedBy(): string {
        const archivedById = this.event.archived_by;
        if (!archivedById) return 'Unknown';

        // Try to find the user in the loaded org users
        const user = this.availableUsers.find(u => u.id === archivedById);
        if (user) {
            return user.name || user.username || user.email || archivedById;
        }

        // If it's the current user, use their display name
        if (this.authService.appMemberID === archivedById) {
            return this.authService.currentUser?.name ||
                this.authService.currentUser?.nickname ||
                archivedById;
        }

        // Fallback: try to make the ID more readable
        // Auth0 IDs look like "auth0|12345" - not user friendly, so just say "a user"
        if (archivedById.startsWith('auth0|')) {
            return 'a user';
        }

        return archivedById;
    }

    /**
     * Get the timestamp of when the event was archived
     */
    getArchivedAt(): string | null {
        return this.event.archived_at || null;
    }

    /**
     * Get the reason for archiving
     */
    getArchiveReason(): string | null {
        return this.event.archive_reason || null;
    }

    /**
     * Check if this is a downtime or error event
     */
    isDowntimeOrErrorEvent(): boolean {
        return this.event.type === 'downtime' || this.event.type === 'incident';
    }

    /**
     * Check if troubleshooting has started
     */
    hasTroubleshootingStarted(): boolean {
        return !!this.event.troubleshooting_started_at;
    }

    getUserDisplayName(userId?: string, preferredName?: string): string {
        if (preferredName && !preferredName.startsWith('auth0|')) {
            return preferredName;
        }

        const user = this.availableUsers.find(u => u.id === userId || u.id === preferredName);
        if (user) {
            return user.name || user.email || user.username || user.id;
        }

        return preferredName || userId || '';
    }

    private displayNameForAssignment(userId: string | null | undefined, preferredName: string | null | undefined): string {
        return this.cleanUserDisplayName(preferredName, userId)
            || this.displayNameFromUserList(userId, preferredName)
            || 'Unknown user';
    }

    private displayNameFromUserList(userId?: string | null, preferredName?: string | null): string | null {
        const user = this.availableUsers.find(u => u.id === userId || u.id === preferredName);
        if (!user) return null;
        return this.cleanUserDisplayName(user.name, user.id)
            || this.cleanUserDisplayName(user.username, user.id)
            || this.cleanUserDisplayName(user.email, user.id);
    }

    private cleanUserDisplayName(value: string | null | undefined, userId?: string | null): string | null {
        const name = value?.trim();
        if (!name || this.isRawUserIdentifier(name, userId)) return null;
        return name;
    }

    private isRawUserIdentifier(value: string, userId?: string | null): boolean {
        const lower = value.toLowerCase();
        const suffix = userId?.includes('|') ? userId.substring(userId.indexOf('|') + 1) : userId;
        return lower.startsWith('auth0|')
            || lower.startsWith('google-oauth2|')
            || (!!userId && value === userId)
            || (!!suffix && value === suffix)
            || /^[0-9a-f]{12,}(\.\.\.)?$/i.test(value);
    }

    /**
     * Check if troubleshooting has been resolved
     */
    hasTroubleshootingResolved(): boolean {
        return !!this.event.resolved_at;
    }

    /**
     * Check if event has a batch
     */
    hasBatch(): boolean {
        return !!this.event.batch;
    }

    /**
     * Check if this event's batch can be completed
     * Returns true only when all conditions are met for completing a job:
     * - Event is a BATCH type
     * - Event is ongoing (no end_ts)
     * - Has a linked batch
     * - Batch is in progress (result === 'running')
     */
    isBatchInProgress(): boolean {
        if (this.event.type !== 'batch') return false;
        if (this.event.end_ts) return false; // Event already completed
        if (!this.hasBatch()) return false;
        if (!this.event.batch) return false;
        // Most important: batch must be in progress (not already completed)
        return this.event.batch.result === 'running';
    }

    /**
     * Check if this event's batch is in progress (for display purposes)
     * Returns true for RUN events with a linked batch that has no end_ts
     */
    canCompleteBatchFromEvent(): boolean {
        return this.isBatchInProgress();
    }

    /**
     * Open complete batch modal (same as dashboard quick action)
     * Reuses the existing CompleteLoadModalComponent
     */
    openCompleteBatchModal(): void {
        // Guard: Cannot complete if batch is not in progress
        if (!this.canCompleteBatchFromEvent()) {
            console.warn('⚠️ [ViewEventModal] Cannot complete batch - not in progress or already completed');
            this.snackBar.open('This job cannot be completed', 'Dismiss');
            return;
        }

        if (!this.event.batch) {
            console.error('❌ [ViewEventModal] No batch object found');
            return;
        }

        // Verify batch is actually in_progress before opening modal
        if (this.event.batch.result !== 'running') {
            console.warn('⚠️ [ViewEventModal] Job already completed with result:', this.event.batch.result);
            this.snackBar.open(`Job already completed: ${this.event.batch.result}`, 'Dismiss');
            return;
        }

        // Fetch equipment for labels/settings, then fetch all running batches
        this.oeeDataService.getEquipmentById(this.event.equipment_id).subscribe({
            next: (equipment) => {
                const equipmentName = equipment?.name || `Equipment ${this.event.equipment_id}`;

                // Fetch all running batches for multi-batch selection
                this.oeeDataService.getCurrentBatches(this.event.equipment_id).subscribe({
                    next: (allBatches) => {
                        allBatches.sort((a, b) => new Date(a.start_ts).getTime() - new Date(b.start_ts).getTime());
                        this.openCompleteBatchDialog(this.event.batch!, allBatches, equipmentName, equipment);
                    },
                    error: () => {
                        this.openCompleteBatchDialog(this.event.batch!, [this.event.batch!], equipmentName, equipment);
                    }
                });
            },
            error: (error) => {
                console.warn('[ViewEventModal] Could not fetch equipment, opening modal without default:', error);
                this.oeeDataService.getCurrentBatches(this.event.equipment_id).subscribe({
                    next: (allBatches) => {
                        allBatches.sort((a, b) => new Date(a.start_ts).getTime() - new Date(b.start_ts).getTime());
                        this.openCompleteBatchDialog(this.event.batch!, allBatches, `Equipment ${this.event.equipment_id}`);
                    },
                    error: () => {
                        this.openCompleteBatchDialog(this.event.batch!, [this.event.batch!], `Equipment ${this.event.equipment_id}`);
                    }
                });
            }
        });
    }

    /**
     * Open the complete batch dialog with the given data
     * The modal handles completion internally — afterClosed just refreshes local state
     */
    private openCompleteBatchDialog(
        batch: Batch,
        allBatches: Batch[],
        equipmentName: string,
        equipment?: any
    ): void {
        const dialogRef = this.dialog.open(CompleteBatchModalComponent, {
            width: '650px',
            panelClass: 'oee-mobile-modal',
            disableClose: false,
            data: {
                batch: batch,
                allBatches: allBatches.length > 1 ? allBatches : undefined,
                equipmentName: equipmentName,
                equipmentId: this.event.equipment_id,
                eventId: this.event.event_id,
                completeBatchText: equipment?.complete_batch_text ?? this.equipmentDetails?.complete_batch_text,
                siteId: this.oeeDataService.getSelectedSiteId() || undefined,
                allowsBatchOperators: equipment?.allows_batch_operators,
                restrictBatchEquipment: equipment?.restrict_batch_equipment
            }
        });

        dialogRef.afterClosed().subscribe((result: { success?: boolean, aborted?: boolean, end_ts?: string, abort_reason_id?: string } | undefined) => {
            if (result?.success) {
                // Modal completed the batch — update local state
                if (this.event.batch) {
                    this.event.batch.result = 'completed';
                    this.event.batch.end_ts = new Date().toISOString();
                }
                this.batchCompleted = true;
                this.batchCompletedEvent.emit(this.event.batch!);
                this.initEditForm();
            } else if (result?.aborted) {
                // User aborted instead — update local state to reflect aborted batch
                if (this.event.batch) {
                    this.event.batch.result = 'aborted';
                    this.event.batch.status = 'aborted';
                    this.event.batch.end_ts = result.end_ts || new Date().toISOString();
                    this.event.batch.abort_reason_id = result.abort_reason_id;
                }
                this.batchCompleted = true;
                this.batchCompletedEvent.emit(this.event.batch!);
                this.initEditForm();
            }
        });
    }

    /**
     * Open abort batch modal (Issue #2677)
     * For aborting a running batch with a required reason
     */
    openAbortBatchModal(): void {
        // Guard: Cannot abort if batch is not in progress
        if (!this.isBatchInProgress()) {
            console.warn('⚠️ [ViewEventModal] Cannot abort batch - not in progress');
            this.snackBar.open('This job cannot be aborted', 'Dismiss');
            return;
        }

        if (!this.event.batch) {
            console.error('❌ [ViewEventModal] No batch object found');
            return;
        }

        const dialogRef = this.dialog.open(AbortBatchModalComponent, {
            width: '550px',
            panelClass: 'oee-mobile-modal',
            disableClose: false,
            data: {
                batch: this.event.batch,
                equipmentName: this.event.equipment_name || `Equipment ${this.event.equipment_id}`,
                equipmentId: this.event.equipment_id,
                siteId: this.oeeDataService.getSelectedSiteId()
            }
        });

        dialogRef.afterClosed().subscribe((result: AbortBatchModalResult | undefined) => {
            if (result?.aborted) {
                // Update local state to reflect aborted batch using server-returned values
                if (this.event.batch) {
                    this.event.batch.result = 'aborted';
                    this.event.batch.status = 'aborted';
                    this.event.batch.end_ts = result.end_ts || new Date().toISOString();
                    this.event.batch.abort_reason_id = result.abort_reason_id;
                }
                this.batchCompleted = true; // Triggers dashboard refresh
                this.batchCompletedEvent.emit(this.event.batch!);
                this.initEditForm();
            }
        });
    }

    /**
     * Open abort batch modal for a specific batch
     */
    openAbortBatchModalForBatch(batch: Batch): void {
        if (batch.result !== 'running') {
            console.warn('[ViewEventModal] Cannot abort batch - not running');
            return;
        }

        const dialogRef = this.dialog.open(AbortBatchModalComponent, {
            width: '550px',
            panelClass: 'oee-mobile-modal',
            disableClose: false,
            data: {
                batch: batch,
                equipmentName: this.event.equipment_name || `Equipment ${this.event.equipment_id}`,
                equipmentId: this.event.equipment_id,
                siteId: this.oeeDataService.getSelectedSiteId()
            }
        });

        dialogRef.afterClosed().subscribe((result: AbortBatchModalResult | undefined) => {
            if (result?.aborted) {
                // Update the batch in the batches array
                if (this.event.batches) {
                    const index = this.event.batches.findIndex(b => b.batch_id === batch.batch_id);
                    if (index >= 0) {
                        this.event.batches[index].result = 'aborted';
                        this.event.batches[index].status = 'aborted';
                        this.event.batches[index].end_ts = new Date().toISOString();
                        this.event.batches[index].abort_reason_id = result.abort_reason_id;
                    }
                }
                this.batchCompleted = true;
                this.snackBar.open('Job aborted successfully', 'Dismiss', { duration: 3000 });
            }
        });
    }

    /**
     * Reopen an aborted batch (Issue #2677)
     * Restores the batch back to running status
     */
    reopenBatch(): void {
        if (!this.event.batch || !this.isBatchAborted() || this.reopening) {
            console.warn('[ViewEventModal] Cannot reopen batch - not aborted or already reopening');
            return;
        }

        this.reopening = true;
        const batchId = this.event.batch.batch_id;

        this.oeeDataService.unabortBatch({ batch_id: batchId }).subscribe({
            next: (result) => {
                // Create new batch object to ensure Angular detects the change
                if (this.event.batch) {
                    this.event.batch = {
                        ...this.event.batch,
                        result: 'running',
                        status: 'running',
                        end_ts: undefined,
                        abort_reason_id: undefined,
                        abort_reason: undefined
                    };
                }
                // Also clear end_ts on the event itself (reopened batch is now running)
                (this.event as any).end_ts = undefined;

                this.reopening = false;
                this.batchCompleted = true; // Triggers dashboard refresh
                this.batchCompletedEvent.emit(this.event.batch!);
                this.initEditForm();
                this.snackBar.open('Job reopened successfully', 'Dismiss', { duration: 3000 });
            },
            error: (error) => {
                this.reopening = false;
                console.error('❌ [ViewEventModal] Failed to reopen batch:', error);
                this.snackBar.open('Failed to reopen job', 'Dismiss', { duration: 5000 });
            }
        });
    }

    /**
     * Reopen a specific aborted batch from the batches list
     */
    reopenBatchForBatch(batch: Batch): void {
        if (batch.result !== 'aborted' && batch.status !== 'aborted') {
            console.warn('[ViewEventModal] Cannot reopen batch - not aborted');
            return;
        }

        this.oeeDataService.unabortBatch({ batch_id: batch.batch_id }).subscribe({
            next: (result) => {
                // Create new array with updated batch to ensure Angular detects the change
                if (this.event.batches) {
                    this.event.batches = this.event.batches.map(b => {
                        if (b.batch_id === batch.batch_id) {
                            return {
                                ...b,
                                result: 'running',
                                status: 'running',
                                end_ts: undefined,
                                abort_reason_id: undefined,
                                abort_reason: undefined
                            };
                        }
                        return b;
                    });
                }
                this.batchCompleted = true;
                this.snackBar.open('Job reopened successfully', 'Dismiss', { duration: 3000 });
            },
            error: (error) => {
                console.error('❌ [ViewEventModal] Failed to reopen batch:', error);
                this.snackBar.open('Failed to reopen job', 'Dismiss', { duration: 5000 });
            }
        });
    }

    /**
     * Check if the batch is aborted and can be resumed
     * Checks both result and status fields for compatibility
     */
    isBatchAborted(): boolean {
        if (this.event.type !== 'batch') return false;
        if (!this.event.batch) return false;
        // Check both result and status - either being 'aborted' means the batch is aborted
        return this.event.batch.result === 'aborted' || this.event.batch.status === 'aborted';
    }

    /**
     * Get troubleshooting status display
     */
    getTroubleshootingStatus(): 'Not Started' | 'In Progress' | 'Resolved' | null {
        if (!this.isDowntimeOrErrorEvent()) return null;

        if (this.event.resolved_at) return 'Resolved';
        if (this.event.troubleshooting_started_at) return 'In Progress';
        return 'Not Started';
    }

    /**
     * Get troubleshooting status badge class
     */
    getTroubleshootingStatusClass(): string {
        const status = this.getTroubleshootingStatus();
        switch (status) {
            case 'Not Started': return 'status-open';
            case 'In Progress': return 'status-in-progress';
            case 'Resolved': return 'status-resolved';
            default: return '';
        }
    }

    /**
     * Calculate TIME TO ADDRESS (from downtime start to troubleshooting start)
     */
    getTimeToAddressMinutes(): number | null {
        if (!this.event.start_ts || !this.event.troubleshooting_started_at) return null;

        const downtimeStart = new Date(this.event.start_ts).getTime();
        const troubleshootStart = new Date(this.event.troubleshooting_started_at).getTime();

        return Math.round((troubleshootStart - downtimeStart) / (1000 * 60));
    }

    /**
     * Calculate TIME TO FIX (from downtime start to resolution)
     */
    getTimeToFixMinutes(): number | null {
        if (!this.event.start_ts || !this.event.resolved_at) return null;

        const downtimeStart = new Date(this.event.start_ts).getTime();
        const resolved = new Date(this.event.resolved_at).getTime();

        return Math.round((resolved - downtimeStart) / (1000 * 60));
    }

    /**
     * Calculate total troubleshooting time (from troubleshooting start to resolution)
     */
    getTroubleshootingDuration(): number | null {
        if (!this.event.troubleshooting_started_at) return null;

        const startTime = new Date(this.event.troubleshooting_started_at).getTime();
        const endTime = this.event.resolved_at
            ? new Date(this.event.resolved_at).getTime()
            : new Date().getTime();

        return Math.round((endTime - startTime) / (1000 * 60)); // minutes
    }

    /**
     * Check if the batch result is set to 'failed' (for conditional failure reason field)
     */
    isBatchResultFailed(): boolean {
        return this.editForm?.get('result')?.value === 'failed';
    }

    /**
     * Check if this event has multiple batches
     */
    hasMultipleBatches(): boolean {
        return (this.event.batches?.length || 0) > 1;
    }

    /**
     * Check if this event has any batches
     */
    hasBatches(): boolean {
        return (this.event.batches?.length || 0) > 0;
    }

    /**
     * Toggle batches section expanded/collapsed state
     */
    toggleBatchesSection(): void {
        this.batchesSectionExpanded = !this.batchesSectionExpanded;
    }

    /**
     * Check if an edit section is expanded (defaults to true/expanded)
     */
    isEditSectionExpanded(section: string): boolean {
        return this.editSectionExpanded[section] !== false;
    }

    /**
     * Toggle an edit section's expanded/collapsed state
     */
    toggleEditSection(section: string): void {
        this.editSectionExpanded[section] = !this.isEditSectionExpanded(section);
    }

    /**
     * Get running batches count
     */
    getRunningBatchesCount(): number {
        return this.event.batches?.filter(b => b.result === 'running').length || 0;
    }

    /**
     * Get completed jobs count
     */
    getCompletedBatchesCount(): number {
        return this.event.batches?.filter(b => b.result === 'completed').length || 0;
    }

    /**
     * Get batch status display text
     */
    getBatchStatusDisplay(batch: Batch): string {
        switch (batch.result) {
            case 'running':
            case 'in_progress':
                return 'Active';
            case 'idle':
                return 'Idle';
            case 'ready_for_review':
                return 'Ready for Review';
            case 'approved':
                return 'Approved';
            case 'completed':
                return 'Completed';
            case 'aborted':
                return 'Aborted';
            case 'cancelled':
                return 'Cancelled';
            case 'unstarted':
            case 'pending':
                return 'Unstarted';
            default:
                return batch.result || 'Unknown';
        }
    }

    /**
     * Get batch status CSS class
     */
    getLinkedBatchLabel(batch: any): string {
        const s = batch.status;
        if (s === 'running' || s === 'in_progress') return 'Active';
        if (s === 'idle') return 'Idle';
        if (s === 'ready_for_review') return 'Ready for Review';
        if (s === 'approved') return 'Approved';
        if (s === 'completed') return 'Completed';
        if (s === 'aborted') return 'Aborted';
        if (s === 'cancelled') return 'Cancelled';
        if (s === 'unstarted' || s === 'pending') return 'Unstarted';
        return s || '';
    }

    getBatchStatusClass(batch: Batch): string {
        switch (batch.result) {
            case 'running':
            case 'in_progress':
                return 'status-running';
            case 'idle':
                return 'status-idle';
            case 'ready_for_review':
                return 'status-ready-for-review';
            case 'approved':
                return 'status-approved';
            case 'completed':
                return 'status-idle';
            case 'aborted':
                return 'status-aborted';
            case 'cancelled':
                return 'status-aborted';
            case 'unstarted':
            case 'pending':
                return 'status-unstarted';
            default:
                return '';
        }
    }

    /**
     * Open complete batch modal for a specific batch
     */
    openCompleteBatchModalForBatch(batch: Batch): void {
        if (batch.result !== 'running') {
            console.warn('[ViewEventModal] Cannot complete batch - not running');
            return;
        }

        // Fetch equipment for labels/settings, then fetch all running batches
        this.oeeDataService.getEquipmentById(this.event.equipment_id).subscribe({
            next: (equipment) => {
                const equipmentName = equipment?.name || this.event.equipment_name || `Equipment ${this.event.equipment_id}`;

                this.oeeDataService.getCurrentBatches(this.event.equipment_id).subscribe({
                    next: (allBatches) => {
                        allBatches.sort((a, b) => new Date(a.start_ts).getTime() - new Date(b.start_ts).getTime());
                        // Use the clicked batch as the pre-selected one
                        const selected = allBatches.find(b => b.batch_id === batch.batch_id) || batch;
                        this.openCompleteBatchDialog(selected, allBatches, equipmentName, equipment);
                    },
                    error: () => {
                        this.openCompleteBatchDialog(batch, [batch], equipmentName, equipment);
                    }
                });
            },
            error: () => {
                const equipmentName = this.event.equipment_name || `Equipment ${this.event.equipment_id}`;
                this.oeeDataService.getCurrentBatches(this.event.equipment_id).subscribe({
                    next: (allBatches) => {
                        allBatches.sort((a, b) => new Date(a.start_ts).getTime() - new Date(b.start_ts).getTime());
                        const selected = allBatches.find(b => b.batch_id === batch.batch_id) || batch;
                        this.openCompleteBatchDialog(selected, allBatches, equipmentName);
                    },
                    error: () => {
                        this.openCompleteBatchDialog(batch, [batch], equipmentName);
                    }
                });
            }
        });
    }

    get completeBatchActionLabel(): string {
        const customLabel = this.equipmentDetails?.complete_batch_text?.trim();
        return customLabel || 'Complete Job';
    }

    /**
     * Get total parts across all batches
     */
    getTotalPartsAcrossBatches(): number {
        return this.event.batches?.reduce((sum, b) => sum + (b.part_count || 0), 0) || 0;
    }

    /**
     * Get total good parts across all batches
     */
    getTotalGoodPartsAcrossBatches(): number {
        return this.event.batches?.reduce((sum, b) => sum + (b.good_parts || 0), 0) || 0;
    }

    /**
     * Get total reject parts across all batches
     */
    getTotalRejectPartsAcrossBatches(): number {
        return this.event.batches?.reduce((sum, b) => sum + (b.reject_parts || 0), 0) || 0;
    }

    /**
     * Get custom field label for display.
     */
    getCustomFieldDisplayLabel(fieldValue: BatchCustomFieldValue): string {
        return fieldValue.field_label;
    }

    /**
     * Format custom field value for at-a-glance display.
     * Handles different field types appropriately.
     */
    formatCustomFieldValue(fieldValue: BatchCustomFieldValue): string {
        if (!fieldValue || fieldValue.value === null || fieldValue.value === undefined) {
            return '—';
        }

        const value = fieldValue.value;
        const fieldType = fieldValue.field_type;

        switch (fieldType) {
            case 'checkbox':
                return value === 'true' ? 'Yes' : 'No';
            case 'date':
                // Format date values nicely
                if (value) {
                    try {
                        const date = new Date(value);
                        return date.toLocaleDateString();
                    } catch {
                        return value;
                    }
                }
                return '—';
            case 'number':
                // Format numbers with locale
                if (value !== '' && !isNaN(Number(value))) {
                    return Number(value).toLocaleString();
                }
                return value || '—';
            default:
                // Text, dropdown, etc. - truncate if too long
                const strValue = String(value);
                if (strValue.length > 30) {
                    return strValue.substring(0, 27) + '...';
                }
                return strValue || '—';
        }
    }

    ngOnDestroy(): void {
        this.formDestroy$.next();
        this.formDestroy$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ===== PRINT WORK ORDER METHODS =====

    /**
     * Format event ID as CMWO (Corrective Maintenance Work Order) number.
     * Uses full event ID, uppercase, prefixed with "CMWO-".
     */
    formatCMWONumber(): string {
        return 'CMWO-' + this.event.event_id.toUpperCase();
    }

    /**
     * Get formatted start timestamp for work order.
     */
    getDowntimeStartDisplay(): string {
        return this.event.start_ts ? this.formatTimestamp(this.event.start_ts) : '';
    }

    /**
     * Get formatted end timestamp for work order.
     */
    getDowntimeEndDisplay(): string {
        return this.event.end_ts ? this.formatTimestamp(this.event.end_ts) : 'Ongoing';
    }

    /**
     * Get formatted repair start timestamp (troubleshooting started).
     */
    getRepairStartDisplay(): string {
        return this.event.troubleshooting_started_at ? this.formatTimestamp(this.event.troubleshooting_started_at) : '';
    }

    /**
     * Get formatted repair end timestamp (resolved at).
     */
    getRepairEndDisplay(): string {
        return this.event.resolved_at ? this.formatTimestamp(this.event.resolved_at) : '';
    }

    /**
     * Get equipment code for work order.
     * Uses equipment_number if set, falls back to equipment code, then event data.
     */
    getEquipmentCode(): string {
        return this.equipmentDetails?.equipment_number || this.equipmentDetails?.code || this.event.equipment_code || 'N/A';
    }

    /**
     * Get equipment location for work order (uses system_name).
     * Falls back to 'N/A' if system_name is not available.
     */
    getEquipmentLocation(): string {
        return this.equipmentDetails?.system_name || 'N/A';
    }

    /**
     * Get downtime reason display for work order.
     * Shows code + display_name if available.
     */
    getDowntimeReasonDisplay(): string {
        if (!this.event.downtime_reason) return 'N/A';
        const reason = this.event.downtime_reason;
        if (reason.code && reason.display_name) {
            return `${reason.code} - ${reason.display_name}`;
        }
        return reason.display_name || reason.code || 'N/A';
    }

    /**
     * Get incident reason display for work order.
     * Shows code + display_name if available.
     */
    getIncidentReasonDisplay(): string {
        const incidentEvent = this.event as any;
        if (!incidentEvent.incident_reason) return 'N/A';
        const reason = incidentEvent.incident_reason;
        if (reason.code && reason.display_name) {
            return `${reason.code} - ${reason.display_name}`;
        }
        return reason.display_name || reason.code || 'N/A';
    }

    /**
     * Get reason display for work order (handles both downtime and incident).
     */
    getReasonDisplay(): string {
        if (this.event.type === 'incident') {
            return this.getIncidentReasonDisplay();
        }
        return this.getDowntimeReasonDisplay();
    }

    /**
     * Get reason label for work order (Downtime Reason vs Incident Reason).
     */
    getReasonLabel(): string {
        return 'Reason Code';
    }

    /**
     * Get problem description for work order.
     * Shows notes and troubleshooting notes (downtime reason shown separately in field 7).
     */
    getProblemDescription(): string {
        const parts: string[] = [];

        // Add general notes if available
        if (this.event.notes) {
            parts.push(this.event.notes);
        }

        // Add troubleshooting notes if available
        if (this.event.troubleshooting_notes) {
            parts.push(this.event.troubleshooting_notes);
        }

        return parts.join('\n\n') || '';
    }

    /**
     * Get current date/time formatted for work order print timestamp.
     */
    getCurrentDateTime(): string {
        return new Date().toLocaleString();
    }

    /**
     * Get employee number for the person who created the event.
     */
    getRequestedByEmployeeNumber(): string {
        if (!this.event.created_by) return '';
        const user = this.availableUsers.find(u => u.id === this.event.created_by);
        return user?.employee_number || '';
    }

    /**
     * Get employee number for the technician.
     */
    getTechnicianEmployeeNumber(): string {
        if (!this.event.troubleshooting_started_by) return '';
        const user = this.availableUsers.find(u => u.id === this.event.troubleshooting_started_by);
        return user?.employee_number || '';
    }

    /**
     * Show the work order form for editing before printing.
     */
    printWorkOrder(): void {
        this.showWorkOrderForm = true;
    }

    /**
     * Go back from work order form to event view.
     */
    cancelWorkOrder(): void {
        this.showWorkOrderForm = false;
    }

    /**
     * Print the work order form.
     */
    confirmPrint(): void {
        window.print();
    }

    /**
     * Load operators and equipment for a batch event (derived from time entries)
     */
    private loadBatchAssignments(): void {
        if (!this.event.event_id) return;

        this.oeeDataService.getBatchTimeEntries(this.event.event_id).subscribe({
            next: (entries) => {
                // Derive unique operators from time entries.
                //
                // Names come from the server (out_user_name, joined from users.name)
                // so we never have to render a raw auth0|... / google-oauth2|... id
                // when the org-user list races or an operator has been deactivated.
                // Null name → 'Unknown user'; the user_id stays internal as a Map key.
                const userMap = new Map<string, { user_id: string; display_name: string }>();
                entries.filter(e => e.out_user_id).forEach(e => {
                    if (!userMap.has(e.out_user_id!)) {
                        userMap.set(e.out_user_id!, {
                            user_id: e.out_user_id!,
                            display_name: this.displayNameForAssignment(e.out_user_id, e.out_user_name)
                        });
                    }
                });
                this.batchOperators = Array.from(userMap.values());

                // Derive unique equipment from time entries
                const equipMap = new Map<string, { equipment_id: string; equipment_name: string; equipment_code: string }>();
                entries.filter(e => e.out_equipment_id).forEach(e => {
                    if (!equipMap.has(e.out_equipment_id!)) {
                        equipMap.set(e.out_equipment_id!, {
                            equipment_id: e.out_equipment_id!,
                            equipment_name: e.out_equipment_name || '',
                            equipment_code: e.out_equipment_code || ''
                        });
                    }
                });
                this.batchEquipment = Array.from(equipMap.values());
            },
            error: (err) => console.error('Failed to load batch time entries:', err)
        });

        // Load worker notes across all stations for this job
        this.oeeDataService.getWorkerNotesForBatch(this.event.event_id).subscribe({
            next: (notes) => {
                this.workerNotes = notes.map(n => ({
                    // Same auth0-id-leak guard as batchOperators above: trust the
                    // server-supplied name; surface 'Unknown user' when null.
                    display_name: this.displayNameForAssignment(n.out_user_id, n.out_user_name),
                    notes: n.out_notes,
                    start_ts: n.out_start_ts,
                    end_ts: n.out_end_ts,
                    equipment_name: n.out_equipment_name || n.out_station_name || null
                }));
            },
            error: (err) => console.error('Failed to load worker notes:', err)
        });
    }
}
